// The ONE shared service-record panel (Session ADE). Renders a completed (or submitted) visit's
// full record -- checklist, defects, parts, notes, time on site, risk assessment, profitability,
// signature, invoice link and photos -- plus every action: Download Service Record PDF, Send now,
// office approval, archive/delete/reactivate, and INLINE editing (the amendment flow
// that used to live in the retired AmendJobSheetModal). Used in BOTH places the office opens a
// record: the Service Records / Call-outs expanded row (JobSheetList) and the lift detail page's
// Service section (ServiceRecordInline), so the record reads and behaves identically everywhere.
//
// Editing is a PRESENTATION move only: it reuses the existing amend endpoint verbatim, so the
// amendment audit trail, the optional reason, the amended-since-sent flag and the DRAFT-invoice
// review flag all keep working exactly as before. Save enables only when something actually
// changed (the server's 400 no_changes guard stays as the backstop), and unsaved edits are
// protected on navigate-away (route blocker + beforeunload) and on panel collapse (the parents'
// DiscardChangesDialog, driven by onDirtyChange).
import { AddLolerReportModal } from "./AddLolerReportModal";
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, UNSAFE_DataRouterContext, useBlocker } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { JobSheetListItem, JobSheetChecklistEntry, JobSheetPartUsed, JobProfitability, RemedialDefectOffer as RemedialDefectOfferItem } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { JobPhotoGallery } from "./JobPhotoGallery";
import { JobPhotoUploader } from "./JobPhotoUploader";
import { AreaChecklist } from "../../components/AreaChecklist";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { LinkInvoiceModal } from "../../components/LinkInvoiceModal";
import { ModalShell } from "../../components/ModalShell";
import { ReopenApprovalModal } from "./ReopenApprovalModal";
import { TechVisitOverageControl } from "./TechVisitOverageControl";
import { useOptionalAuthUser } from "../../lib/auth";
import { ReportSendModal, SentOutsideModal } from "./ReportSendModal";
import { RemedialDefectOffer } from "./RemedialDefectOffer";
import { durationMinutes, formatDurationHm } from "../../lib/timeOnSite";
import { isoToLocalYmd, isoToLocalHHMM, isoPairSpansMidnight, buildArrivalDeparture, combineDateAndTime, daysBetweenLocalDates } from "../../lib/calloutSheet";
import { labourCostDisplay } from "../../lib/profitabilityDisplay";
import { groupStoredChecklistByArea, buildAreaGroups, confirmableAreas, allAreasConfirmed, itemsUseServiceAreas, type StoredAreaGroup, type AreaGroup } from "../../lib/serviceAreas";
import type { ChecklistItemDef, ChecklistCategory, ChecklistStatus } from "../../lib/offline/types";
import { formatDate, formatTime, gbpExact } from "../../lib/format";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill, toneTextClass } from "../../components/StatusPill";
import { lateSubmissionDays } from "../../lib/visitDateWindow";
import { statusEntry } from "../../lib/statusRegistry";
import { RISK_ASSESSMENT_HAZARDS, riskAssessmentFormError } from "../../lib/riskAssessmentHazards";

// ---- Shared formatting (moved from JobSheetList with the panel) ----

// Pence -> "£x.xx", or "Not recorded" when the figure could not be calculated (Session AK Item 2).
function gbpPence(p: number | null): string {
  return p == null ? "Not recorded" : gbpExact(p / 100);
}

function fmtMinutes(mins: number | null): string {
  if (mins == null || mins <= 0) return "-";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

// The colour of a checklist result word comes from the registry's `checkResult` domain (the page keeps
// its own words, "Pass" / "Fail" / "Advisory" / "N/A", below).
function checkWordClass(status: string): string {
  return toneTextClass(statusEntry("checkResult", status).tone);
}

// Plain English for the office (house rule -- never show the raw enum key).
// Mirrors the engineer JobDetail labels so the two surfaces read the same.
const CHECK_LABEL: Record<string, string> = { pass: "Pass", fail: "Fail", advisory: "Advisory", na: "N/A" };

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "advisory", label: "Advisory" },
  { value: "na", label: "N/A" },
];

// The fixed lift-status options an engineer picks from on a call-out (mirrors the server list).
const LIFT_STATUS_OPTIONS = [
  "Lift running normally",
  "Lift running with restrictions",
  "Lift taken out of service",
  "Awaiting parts",
  "Awaiting further instruction",
] as const;

const editInputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

// Mirror the server's retain logic so dialogs can be framed accurately (amber Archive for a
// retained record, red Delete for a content-free stub). Shared by the panel's own delete
// control and JobSheetList's quick per-row delete.
export function sheetWillArchive(sheet: JobSheetListItem): boolean {
  const hasWorkContent =
    sheet.checklist.length > 0 ||
    sheet.defects.length > 0 ||
    sheet.partsUsed.length > 0 ||
    !!sheet.advisoryNotes?.trim() ||
    !!sheet.engineerNotes?.trim() ||
    !!sheet.faultFound?.trim() ||
    !!sheet.workCarriedOut?.trim();
  // FR4: a record whose approval was ever reopened is archived by the server (jobSheetDeleteMode approvalReopened), whatever it holds now.
  return sheet.completedAt != null || sheet.sentToClientAt != null || sheet.invoiced || hasWorkContent || sheet.approvalReopened === true;
}

// ---- Inline-edit state + the pure dirty check ----

// Editable rows carry a local key so add/remove never confuses React (the server never sees it).
interface PartRow extends JobSheetPartUsed {
  _k: number;
}
interface DefectRow {
  _k: number;
  text: string;
}

let uid = 0;
const nextKey = () => ++uid;

// The normalised comparable shape of the amendable fields. Mirrors the SERVER's change
// summariser semantics (jobsheets.ts summariseSheetChanges) so the Save button enables exactly
// when the server would record a change: checklist compares item -> status|note; defects and
// parts compare as MULTISETS (reordering rows alone is not a change); notes compare trimmed.
export interface AmendEditState {
  checklist: JobSheetChecklistEntry[];
  defects: string[]; // raw row text (normalised inside the compare)
  parts: JobSheetPartUsed[];
  advisoryNotes: string;
  engineerNotes: string;
  faultFound: string;
  workCarriedOut: string;
  liftStatusOnLeaving: string;
  // Visit times + date (Session ADJ): form-level values ("YYYY-MM-DD" / "HH:MM" / "" when
  // unset) so the office can enter or correct what the engineer did not capture. Apply to
  // BOTH sheet kinds (a call-out's times drive its labour, a service visit's its duration).
  visitDateYmd: string;
  arrivalHHMM: string;
  departureHHMM: string;
}

function normParts(parts: JobSheetPartUsed[]): string[] {
  return parts
    .filter((p) => p.description.trim().length > 0)
    .map(
      (p) =>
        `${(p.partCode ?? "").trim()}|${p.description.trim()}|${Math.max(1, Math.round(p.quantity || 1))}|${
          typeof p.unitCost === "number" && Number.isFinite(p.unitCost) ? p.unitCost : ""
        }`,
    )
    .sort();
}

function normDefects(defects: string[]): string[] {
  return defects.map((d) => d.trim()).filter((d) => d.length > 0).sort();
}

function checklistFingerprint(list: JobSheetChecklistEntry[]): string {
  return list
    .map((c) => `${c.item}::${c.status}|${(c.note ?? "").trim()}`)
    .sort()
    .join("\n");
}

const normText = (s: string) => s.trim();

// True when the edited state differs from the baseline in a way the amend endpoint would record
// (exported pure so it is unit tested). isCallout scopes the comparison to the fields that sheet
// type actually sends, exactly as the save body does.
export function amendStateDirty(baseline: AmendEditState, edited: AmendEditState, isCallout: boolean): boolean {
  if (normDefects(baseline.defects).join("\n") !== normDefects(edited.defects).join("\n")) return true;
  if (normParts(baseline.parts).join("\n") !== normParts(edited.parts).join("\n")) return true;
  if (normText(baseline.engineerNotes) !== normText(edited.engineerNotes)) return true;
  // Visit times + date apply to both sheet kinds.
  if (baseline.visitDateYmd !== edited.visitDateYmd) return true;
  if (baseline.arrivalHHMM !== edited.arrivalHHMM) return true;
  if (baseline.departureHHMM !== edited.departureHHMM) return true;
  if (isCallout) {
    if (normText(baseline.faultFound) !== normText(edited.faultFound)) return true;
    if (normText(baseline.workCarriedOut) !== normText(edited.workCarriedOut)) return true;
    if (normText(baseline.liftStatusOnLeaving) !== normText(edited.liftStatusOnLeaving)) return true;
  } else {
    if (checklistFingerprint(baseline.checklist) !== checklistFingerprint(edited.checklist)) return true;
    if (normText(baseline.advisoryNotes) !== normText(edited.advisoryNotes)) return true;
  }
  return false;
}

function baselineFromSheet(sheet: JobSheetListItem): AmendEditState {
  return {
    checklist: sheet.checklist.map((c) => ({ ...c })),
    defects: sheet.defects.slice(),
    parts: sheet.partsUsed.map((p) => ({ ...p })),
    advisoryNotes: sheet.advisoryNotes ?? "",
    engineerNotes: sheet.engineerNotes ?? "",
    faultFound: sheet.faultFound ?? "",
    workCarriedOut: sheet.workCarriedOut ?? "",
    liftStatusOnLeaving: sheet.liftStatusOnLeaving ?? "",
    visitDateYmd: isoToLocalYmd(sheet.completedAt),
    arrivalHHMM: isoToLocalHHMM(sheet.arrivalTime),
    departureHHMM: isoToLocalHHMM(sheet.departureTime),
  };
}

// ---- The office 14-area checklist editor (Session ADO) --------------------------------------
// The office records a passenger ELE-MS-001 checklist through the SAME fourteen-area recording UI
// the engineer uses (src/components/AreaChecklist.tsx): confirm all areas in one action for a
// clean office-assisted record, or drill into an area to flag a specific item, instead of a
// four-screen scroll of ninety-odd rows. The richer editor state (exceptions + per-area
// confirmations + item notes) is DERIVED back into the flat JobSheetChecklistEntry[] the amend
// endpoint already accepts, so the stored payload and the endpoint are unchanged. A non-passenger
// checklist (goods / platform / equipment / legacy) keeps the flat item editor -- the same
// fallback the read view uses. All pure, so the mapping unit tests directly.

// An ordered source item for the area editor: its id, description and (optional) stored category,
// preserved so the derived checklist is byte-identical to the stored one on an untouched load.
export interface OfficeAreaSourceItem {
  id: string;
  item: string;
  category?: string;
}

// Whether a checklist should be edited through the fourteen-area UI: it must be the passenger
// ELE-MS-001 checklist AND every entry must carry its item id (the area editor keys on ids). A
// mixed / id-less checklist falls back to the flat editor, exactly like the read view does.
export function checklistUsesAreaEditor(entries: { id?: string }[]): boolean {
  return entries.length > 0 && entries.every((e) => !!e.id) && itemsUseServiceAreas(entries as { id: string }[]);
}

// The ChecklistItemDef list the area grouping needs (category is display metadata only; the
// grouping keys on the id, so a stored free-text category is coerced without affecting it).
export function officeAreaItems(source: OfficeAreaSourceItem[]): ChecklistItemDef[] {
  return source.map((s) => ({ id: s.id, description: s.item, category: (s.category as ChecklistCategory) ?? "safety" }));
}

// Seed the area-editor state from stored checklist entries (the amend case). Only the exceptions
// (Fail / Advisory / N/A) are recorded as statuses; a stored Pass carries no exception. Every area
// that already holds a recorded item is pre-confirmed, so an untouched load derives back to the
// SAME entries (a stored Pass rides its confirmed area back to Pass) and the panel is not dirty.
export function initOfficeAreaState(entries: JobSheetChecklistEntry[]): {
  statuses: Record<string, ChecklistStatus>;
  notes: Record<string, string>;
  confirmed: Record<string, boolean>;
} {
  const statuses: Record<string, ChecklistStatus> = {};
  const notes: Record<string, string> = {};
  for (const e of entries) {
    if (!e.id) continue;
    if (e.status !== "pass") statuses[e.id] = e.status as ChecklistStatus;
    if (e.note && e.note.trim()) notes[e.id] = e.note;
  }
  const areas = buildAreaGroups(officeAreaItems(entries.map((e) => ({ id: e.id ?? "", item: e.item, category: e.category }))), []);
  const confirmed: Record<string, boolean> = {};
  for (const a of confirmableAreas(areas)) confirmed[a.key] = true;
  return { statuses, notes, confirmed };
}

// Derive the flat checklist the amend endpoint accepts from the area-editor state. For each source
// item, in its original order: a flagged exception wins; otherwise it is a Pass once its area is
// confirmed, or N/A (an honest non-claim) until then -- so nothing records a Pass without a
// deliberate office confirmation (the honesty guard, mirroring the engineer F1 fix). Byte-identical
// to the stored entries on an untouched amend load.
export function deriveOfficeChecklist(
  source: OfficeAreaSourceItem[],
  areas: AreaGroup[],
  statuses: Record<string, ChecklistStatus>,
  confirmed: Record<string, boolean>,
  notes: Record<string, string>,
): JobSheetChecklistEntry[] {
  const areaKeyByItem = new Map<string, string>();
  for (const a of areas) for (const it of a.items) areaKeyByItem.set(it.id, a.key);
  return source.map((s) => {
    const exc = statuses[s.id];
    const key = areaKeyByItem.get(s.id);
    const status: ChecklistStatus = exc ?? (key && confirmed[key] ? "pass" : "na");
    const note = notes[s.id]?.trim();
    return {
      ...(s.id ? { id: s.id } : {}),
      item: s.item,
      status,
      ...(s.category ? { category: s.category } : {}),
      ...(note ? { note } : {}),
    };
  });
}

// The first item the office has flagged Fail WITHOUT a note that is NEW or CHANGED from the stored
// record. A pre-existing note-less Fail left exactly as it was stored is GRANDFATHERED (a legacy /
// migrated record can carry one), so an unrelated amendment is never blocked into fabricating a note
// for a fault the office did not touch. A Fail the office records this session still needs its note
// (mirroring the engineer). Pure.
export function firstUnnotedNewFail(
  source: OfficeAreaSourceItem[],
  statuses: Record<string, ChecklistStatus>,
  notes: Record<string, string>,
  stored: JobSheetChecklistEntry[],
): OfficeAreaSourceItem | null {
  const storedById = new Map<string, { status: string; note?: string }>();
  for (const e of stored) if (e.id) storedById.set(e.id, { status: e.status, note: e.note });
  for (const s of source) {
    if (statuses[s.id] !== "fail" || notes[s.id]?.trim()) continue;
    const prior = storedById.get(s.id);
    const priorSameNotelessFail = prior != null && prior.status === "fail" && !prior.note?.trim();
    if (!priorSameNotelessFail) return s; // a new (or changed) note-less Fail
  }
  return null;
}

// Build the amend-body time fields from the edited form values (Session ADJ). Returns the fields
// to merge into the save body, or an error string. Pure, so the date/time composition is unit
// tested: times re-base onto the edited visit date; a blank time clears the stored one; a genuine
// overnight call-out span (the original pair crossed midnight) is preserved; anything else with
// departure <= arrival is rejected as a typo before it reaches the server.
export function buildTimeAmendment(
  baseline: AmendEditState,
  edited: AmendEditState,
  original: { arrivalTime: string | null; departureTime: string | null },
): { fields?: { arrivalTime?: string | null; departureTime?: string | null; visitDate?: string }; error?: string } {
  const dateChanged = baseline.visitDateYmd !== edited.visitDateYmd;
  const timesChanged = baseline.arrivalHHMM !== edited.arrivalHHMM || baseline.departureHHMM !== edited.departureHHMM;
  if (!dateChanged && !timesChanged) return { fields: {} };

  const baseDate = edited.visitDateYmd || baseline.visitDateYmd;
  if (!baseDate) {
    return { error: "Set the visit date before recording the times." };
  }

  const fields: { arrivalTime?: string | null; departureTime?: string | null; visitDate?: string } = {};
  if (dateChanged) {
    // Midday local: safely inside the same calendar day in UTC year-round in the UK.
    const visitDate = combineDateAndTime(edited.visitDateYmd, "12:00");
    if (!visitDate) return { error: "Enter a valid visit date." };
    fields.visitDate = visitDate;
  }

  const arr = edited.arrivalHHMM;
  const dep = edited.departureHHMM;
  if (arr && dep) {
    const spansMidnight = isoPairSpansMidnight(original.arrivalTime, original.departureTime);
    const pair = buildArrivalDeparture(baseDate, arr, dep, spansMidnight);
    if (!pair) return { error: "Enter valid arrival and departure times." };
    if (new Date(pair.departureISO).getTime() <= new Date(pair.arrivalISO).getTime()) {
      return { error: "The departure time must be after the arrival time." };
    }
    fields.arrivalTime = pair.arrivalISO;
    fields.departureTime = pair.departureISO;
  } else {
    fields.arrivalTime = arr ? combineDateAndTime(baseDate, arr) : null;
    fields.departureTime = dep ? combineDateAndTime(baseDate, dep) : null;
  }
  return { fields };
}

// ---- Unsaved-changes dialog (shared with the parents' collapse guards) ----
// Portalled via ModalShell so it never inherits a list cell's white-space:nowrap.
export function DiscardChangesDialog({ open, onKeep, onDiscard }: { open: boolean; onKeep: () => void; onDiscard: () => void }) {
  if (!open) return null;
  return (
    <ModalShell ariaLabel="Unsaved changes" onClose={onKeep} width="max-w-sm" border="border-amber-500">
      <h3 className="font-heading text-lg text-amber-400">Unsaved changes</h3>
      <p className="mt-2 text-sm text-ele-body">This record has edits that have not been saved. Leave without saving them?</p>
      <div className="mt-4 flex flex-wrap justify-end gap-3">
        <button
          type="button"
          onClick={onDiscard}
          className="max-sm:min-h-11 rounded-ele border border-amber-500 px-4 py-2 font-menu text-xs tracking-wide text-amber-400 transition hover:bg-amber-500/10"
        >
          Discard changes
        </button>
        <Button
          size="sm"
          onClick={onKeep}
        >
          Keep editing
        </Button>
      </div>
    </ModalShell>
  );
}

// Route-navigation guard. useBlocker needs the app's DATA router (createBrowserRouter); component
// tests render under a plain <MemoryRouter>, which has no data-router context, so the guard simply
// does not mount there (the beforeunload guard still covers reload/close). Conditional MOUNTING,
// never a conditional hook.
function NavigationBlocker({ when }: { when: boolean }) {
  const hasDataRouter = useContext(UNSAFE_DataRouterContext) != null;
  if (!hasDataRouter) return null;
  return <DataRouterNavigationBlocker when={when} />;
}

function DataRouterNavigationBlocker({ when }: { when: boolean }) {
  const blocker = useBlocker(when);
  // A completed save/cancel while a block is pending: let the navigation through.
  useEffect(() => {
    if (!when && blocker.state === "blocked") blocker.proceed();
  }, [when, blocker]);
  return <DiscardChangesDialog open={blocker.state === "blocked"} onKeep={() => blocker.reset?.()} onDiscard={() => blocker.proceed?.()} />;
}

// ---- Read-only record sections ----

function ProfitabilitySection({ p }: { p: JobProfitability }) {
  const hours = p.labourHours != null ? `${p.labourHours.toFixed(2)} h` : "Not recorded";
  // Session BDL (F3): labour cost, total cost and margin are built from the engineer's cost rate, which is admin only, so the
  // server leaves them out (the keys are absent) for anyone who is not an admin. Show what remains and say why the rest is gone.
  if (p.marginPence === undefined && p.totalCostPence === undefined && p.labourCostPence === undefined) {
    return (
      <div className="mt-5">
        <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Profitability</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm sm:grid-cols-3">
          <ProfitStat label="Time on site" value={hours} />
          <ProfitStat label="Parts cost" value={gbpPence(p.partsCostPence)} />
          <ProfitStat label="Expenses" value={gbpPence(p.otherCostsPence)} />
          <ProfitStat label="Charge (ex VAT)" value={gbpPence(p.chargePence)} />
        </div>
        <p className="mt-1 text-sm text-ele-body">Labour cost, total cost and margin are shown to admins only.</p>
      </div>
    );
  }
  const marginPct = p.marginPercent != null ? `${p.marginPercent.toFixed(1)}%` : "Not recorded";
  const marginInk = p.marginPence == null ? "text-ele-strong" : toneTextClass(p.marginPence >= 0 ? "green" : "red");
  return (
    <div className="mt-5">
      <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Profitability</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm sm:grid-cols-3">
        <ProfitStat label="Time on site" value={hours} />
        {/* Labour cost distinguishes "No time recorded" (engineer) from "Cost rate not set"
            (office) so it is clear which field to fill in (Session ABU Part D). */}
        <ProfitStat label="Labour cost" value={labourCostDisplay({ labourCostReason: p.labourCostReason, labourCostPence: p.labourCostPence ?? null })} />
        <ProfitStat label="Parts cost" value={gbpPence(p.partsCostPence)} />
        {/* Session ATE: this job's APPROVED engineer expenses (mileage/materials/other), already
            folded into the total below. */}
        <ProfitStat label="Expenses" value={gbpPence(p.otherCostsPence)} />
        <ProfitStat label="Total cost" value={gbpPence(p.totalCostPence ?? null)} />
        <ProfitStat label="Charge (ex VAT)" value={gbpPence(p.chargePence)} />
        <ProfitStat label="Margin" value={gbpPence(p.marginPence ?? null)} tone={marginInk} />
      </div>
      <p className="mt-1 text-sm text-ele-body">
        Margin: <span className={marginInk}>{marginPct}</span>
      </p>
    </div>
  );
}

function ProfitStat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">{label}</div>
      <div className={tone ?? "text-ele-strong"}>{value}</div>
    </div>
  );
}

// Session AQB (D78) / AQW: the additional items THIS CONTRACT covers (gates, doors, barriers,
// shutters that ride the lift's own visits) and what the engineer recorded for each -- NEVER an
// inspection area of the lift itself (the ADDITIONAL ITEMS ON A LIFT CONTRACT rule is explicit that
// a gate is not a section of the lift). Hidden entirely when the sheet carries none, which is every
// record completed before D78 and every contract that covers only lifts.
const COVERED_ITEM_LABEL: Record<string, string> = { pass: "Checked", fail: "Needs attention", na: "No access" };

function CoveredItemsSection({ sheet }: { sheet: JobSheetListItem }) {
  // Defensive against a fixture or cached response that predates this field (coveredItems is a
  // required field on the real DTO, but a hand-built test object or a stale offline cache is not
  // the server): absent reads the same as "none", never a crash.
  const coveredItems = sheet.coveredItems ?? [];
  if (coveredItems.length === 0) return null;
  return (
    <div className="mt-5">
      <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Also covered by this contract</h3>
      <ul className="space-y-1.5">
        {coveredItems.map((item) => (
          <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
            <span className="min-w-0 flex-1 text-ele-strong">
              {item.description}
              {item.maintenanceOnly && <span className="ml-2 text-xs text-ele-body opacity-70">(maintenance only)</span>}
            </span>
            <span className={`shrink-0 font-menu text-xs tracking-wide ${checkWordClass(item.status)}`}>
              {COVERED_ITEM_LABEL[item.status] ?? item.status}
            </span>
            {item.note && <span className="w-full text-xs text-ele-body opacity-70">{item.note}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Session APF (ENGINEER JOB SHEET RULES): the mandatory RAMS acknowledgement, distinct from the
// OPTIONAL Site Risk Assessment rendered by RiskAssessmentSection below. Always shown (never hidden
// when absent), because "not yet acknowledged" is itself the fact the office needs to see.
function RamsAcknowledgementSection({ sheet }: { sheet: JobSheetListItem }) {
  return (
    <div className="mt-5">
      <h3 className="font-heading text-sm uppercase tracking-wide text-ele-blue">RAMS acknowledgement</h3>
      {sheet.ramsAcknowledgedAt ? (
        <p className="mt-1 text-sm text-ele-strong">RAMS acknowledged on {formatDate(sheet.ramsAcknowledgedAt)}.</p>
      ) : (
        <p className="mt-1 text-sm text-ele-body opacity-70">Not yet acknowledged.</p>
      )}
    </div>
  );
}

// Session ASW (Director decision D10): the office adds, corrects or removes a risk assessment on a completed record.
// The SAME one-per-job record the engineer's phone writes, validated by the same rule (control measures once a hazard
// is ticked). Used by the missing-assessment banner (add) and by RiskAssessmentSection (correct an office entry).
function RiskAssessmentEditor({
  jobId,
  initial,
  onSaved,
  onCancel,
}: {
  jobId: string;
  initial: NonNullable<JobSheetListItem["riskAssessment"]> | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [hazards, setHazards] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(RISK_ASSESSMENT_HAZARDS.map((h) => [h.key, initial?.hazards.includes(h.label) ?? false])),
  );
  const [other, setOther] = useState(initial?.otherHazards ?? "");
  const [control, setControl] = useState(initial?.controlMeasures ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const formError = riskAssessmentFormError(hazards, other, control);
    if (formError) {
      setError(formError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.jobsheets.recordRiskAssessment(jobId, { ...hazards, otherHazards: other.trim(), controlMeasures: control.trim() });
      onSaved();
    } catch (err) {
      setError(friendlyApiError(err, "The risk assessment could not be saved."));
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
      <p className="mb-2 text-xs text-ele-body opacity-80">
        Tick any hazards present at this visit and record the control measures. It is saved as added by the office after the visit.
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {RISK_ASSESSMENT_HAZARDS.map((h) => {
          const on = !!hazards[h.key];
          return (
            <label key={h.key} className="flex items-center gap-2 text-sm text-ele-strong">
              <input
                type="checkbox"
                checked={on}
                onChange={() => {
                  setHazards((prev) => ({ ...prev, [h.key]: !prev[h.key] }));
                  if (error) setError(null);
                }}
              />
              {h.label}
            </label>
          );
        })}
      </div>
      <label className="mt-3 block text-sm text-ele-body" htmlFor={`ra-other-${jobId}`}>
        Other hazards
      </label>
      <input
        id={`ra-other-${jobId}`}
        value={other}
        onChange={(e) => {
          setOther(e.target.value);
          if (error) setError(null);
        }}
        className="mt-1 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm"
      />
      <label className="mt-3 block text-sm text-ele-body" htmlFor={`ra-control-${jobId}`}>
        Control measures
      </label>
      <textarea
        id={`ra-control-${jobId}`}
        value={control}
        onChange={(e) => {
          setControl(e.target.value);
          if (error) setError(null);
        }}
        rows={3}
        className="mt-1 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm"
      />
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save risk assessment"}
        </Button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-ele border border-ele-hairline px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-surface-soft disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Session ASW (Director decision D10): a completed service or repair record with NO risk assessment on file is
// flagged near the top of the record, where the office will see it, never buried below the checklist. A flag, never a
// block: the office adds one here. An archived record is read-only, so it shows the notice without the control.
function MissingRiskAssessmentBanner({ sheet, onSaved }: { sheet: JobSheetListItem; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  if (!sheet.riskAssessmentMissing || !sheet.jobId) return null;
  const archived = sheet.archivedAt != null;
  return (
    <div role="status" className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 p-3">
      <p className="font-menu text-xs uppercase tracking-wide text-amber-400">No risk assessment on file</p>
      <p className="mt-1 text-sm text-ele-body">
        No risk assessment was recorded for this visit. You can add one now from the engineer&apos;s account of the visit.
      </p>
      {!archived && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-2 rounded-ele border border-amber-500 px-3 py-2 font-menu text-xs tracking-wide text-amber-400 transition hover:bg-amber-500/10"
        >
          Add risk assessment
        </button>
      )}
      {open && <RiskAssessmentEditor jobId={sheet.jobId} initial={null} onSaved={onSaved} onCancel={() => setOpen(false)} />}
    </div>
  );
}

// Pre-work risk assessment (Session AK Item 9): the "complete" badge, the flagged hazards and the
// control measures the engineer recorded before starting work. Hidden when none was completed.
// Session ASW: an assessment the OFFICE added after the visit says so, and only that one can be corrected or removed
// here; the engineer's on-site record is kept as recorded.
function RiskAssessmentSection({ sheet, onChanged }: { sheet: JobSheetListItem; onChanged: () => void }) {
  const ra = sheet.riskAssessment;
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!ra) return null;
  const officeRecorded = ra.officeRecorded ?? null;
  const canChange = officeRecorded != null && sheet.jobId != null && sheet.archivedAt == null;

  async function remove() {
    if (!sheet.jobId) return;
    setRemoving(true);
    setError(null);
    try {
      await api.jobsheets.removeRiskAssessment(sheet.jobId);
      onChanged();
    } catch (err) {
      setError(friendlyApiError(err, "The risk assessment could not be removed."));
      setRemoving(false);
    }
  }

  return (
    <div className="mt-5">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="font-heading text-sm uppercase tracking-wide text-ele-blue">Risk assessment</h3>
        <StatusPill word="Complete" tone="green" variant="solid" />
      </div>
      {officeRecorded && (
        <p className="mb-2 text-xs text-ele-body opacity-80">
          Added by the office after the visit{officeRecorded.byName ? ` by ${officeRecorded.byName}` : ""}
          {officeRecorded.at ? ` on ${formatDate(officeRecorded.at)}` : ""}.
        </p>
      )}
      {ra.hazards.length === 0 && !ra.otherHazards ? (
        <p className="text-sm text-ele-body">No hazards flagged.</p>
      ) : (
        <ul className="list-disc space-y-0.5 pl-5 text-sm text-ele-strong">
          {ra.hazards.map((h) => (
            <li key={h}>{h}</li>
          ))}
          {ra.otherHazards && <li>{ra.otherHazards}</li>}
        </ul>
      )}
      {ra.controlMeasures && (
        <p className="mt-2 text-sm">
          <span className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Control measures: </span>
          <span className="text-ele-strong">{ra.controlMeasures}</span>
        </p>
      )}
      {canChange && !editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded-ele border border-ele-hairline px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-surface-soft"
          >
            Edit risk assessment
          </button>
          <ConfirmButton
            onConfirm={remove}
            label="Remove risk assessment"
            confirmLabel="Remove it?"
            busy={removing}
            busyLabel="Removing..."
          />
        </div>
      )}
      {editing && sheet.jobId && (
        <RiskAssessmentEditor jobId={sheet.jobId} initial={ra} onSaved={onChanged} onCancel={() => setEditing(false)} />
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </div>
  );
}

// Plain-English label for why a signature was not captured (house rule: never the raw enum key).
const SIGNATURE_SKIP_LABEL: Record<string, string> = {
  customer_unavailable: "No one was available to sign",
  remote_site: "Unattended / remote site",
  emergency: "Emergency: signature not taken",
  // Session AEB: the office recorded this visit after the fact. The OFFICE sees the full
  // provenance here; the customer certificate shows no signature block at all (never this text,
  // never the raw token - fieldLoopPdf suppresses it).
  office_entered_retrospectively: "None: the office entered this record after the visit (backdated). No signature exists for it.",
};

// The office-entered retrospective marker (Session AEB): the visible provenance pill. Keyed on
// the sheet's signatureSkipReason, which the backdated endpoint stamps server-side.
export function isBackdatedOfficeEntry(sheet: { signatureSkipReason: string | null }): boolean {
  return sheet.signatureSkipReason === "office_entered_retrospectively";
}

// Customer signature (Session AK Item 8): the captured signature image (served via the authenticated
// proxy), the signer's name and the completion timestamp; or the recorded reason none was taken.
function SignatureSection({ sheet }: { sheet: JobSheetListItem }) {
  if (!sheet.hasSignature && !sheet.signatureSkipReason && !sheet.signerName) return null;
  return (
    <div className="mt-5">
      <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Customer signature</h3>
      {sheet.hasSignature && sheet.jobId ? (
        <div>
          <img
            src={api.jobs.signatureUrl(sheet.jobId)}
            alt="Customer signature"
            className="max-h-32 rounded-ele border border-ele-purple-accent bg-white p-1"
          />
          <p className="mt-1 text-sm text-ele-body">
            Signed by <span className="text-ele-strong">{sheet.signerName?.trim() || "name not given"}</span>
            {sheet.completedAt ? ` on ${formatDate(sheet.completedAt)}` : ""}.
          </p>
        </div>
      ) : (
        <p className="text-sm text-ele-body">
          {sheet.signatureSkipReason
            ? SIGNATURE_SKIP_LABEL[sheet.signatureSkipReason] ?? sheet.signatureSkipReason
            : "No signature on file."}
        </p>
      )}
    </div>
  );
}

function partsTotal(parts: JobSheetPartUsed[]): number {
  return parts.reduce((sum, p) => sum + (p.unitCost ?? 0) * p.quantity, 0);
}

// The invoice back-reference (Session AO Item 3): the Xero invoice covering this job, linking
// through to the office invoice detail, or "Not yet invoiced" with a manual "Link to invoice"
// action for the historical backlog. Local state gives immediate feedback; the prop re-syncs on
// a list reload. Linking never creates or modifies a Xero invoice.
function InvoiceBackrefSection({ jobId, initial }: { jobId: string; initial: JobSheetListItem["linkedInvoice"] }) {
  const [linked, setLinked] = useState(initial);
  useEffect(() => setLinked(initial), [initial]);
  const [showPick, setShowPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function link(invoiceId: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.jobsheets.linkInvoice(jobId, invoiceId);
      setLinked(res.linkedInvoice);
      setShowPick(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not link the invoice. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function unlink() {
    setBusy(true);
    setError(null);
    try {
      await api.jobsheets.unlinkInvoice(jobId);
      setLinked(null);
    } catch {
      setError("Could not unlink the invoice. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5">
      <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Invoice</h3>
      {linked ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Link to={`/office/invoices/${encodeURIComponent(linked.invoiceCacheId)}`} className="text-ele-green underline-offset-2 hover:underline">
            {linked.invoiceNumber ? `Invoice ${linked.invoiceNumber}` : "View invoice"}
          </Link>
          <button type="button" onClick={unlink} disabled={busy} className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-danger disabled:opacity-50">
            {busy ? "..." : "Unlink"}
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-ele-body">Not yet invoiced.</span>
          <button type="button" onClick={() => { setError(null); setShowPick(true); }} className="font-menu text-xs tracking-wide text-ele-green transition hover:underline">
            Link to invoice
          </button>
        </div>
      )}
      {error && <p role="alert" className="mt-1 text-xs text-ele-danger">{error}</p>}
      <LinkInvoiceModal open={showPick} busy={busy} error={null} onSelect={link} onClose={() => !busy && setShowPick(false)} />
    </div>
  );
}

// Time on site for a SERVICE visit (Session ABU Part B). Call-outs already show this in
// CalloutSummary; every completed visit captures arrival (when the engineer opened the job) and
// departure (on submit). Each cell shows "Not recorded" rather than a blank when the time is
// missing (an older sheet, or one submitted before both were captured).
function TimeOnSiteSection({ sheet }: { sheet: JobSheetListItem }) {
  const arrived = sheet.arrivalTime ? formatTime(sheet.arrivalTime) : "Not recorded";
  const left = sheet.departureTime ? formatTime(sheet.departureTime) : "Not recorded";
  const duration = formatDurationHm(durationMinutes(sheet.arrivalTime, sheet.departureTime));
  return (
    <div className="mt-5">
      <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Time on site</h3>
      <div className="grid grid-cols-3 gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm">
        <div>
          <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Arrived</div>
          <div className="text-ele-strong">{arrived}</div>
        </div>
        <div>
          <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Left</div>
          <div className="text-ele-strong">{left}</div>
        </div>
        <div>
          <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Duration</div>
          <div className="text-ele-strong">{duration}</div>
        </div>
      </div>
    </div>
  );
}

// Editable visit date + arrival/departure times (Session ADJ), shown while amending either sheet
// kind. The office enters or corrects what the engineer did not capture (the two-way fallback:
// whichever side saved last wins); a blank time clears one recorded in error. Minutes preview
// live so a typo is obvious before saving.
function EditableVisitTimes({
  visitDateYmd,
  onVisitDate,
  arrivalHHMM,
  onArrival,
  departureHHMM,
  onDeparture,
}: {
  visitDateYmd: string;
  onVisitDate: (v: string) => void;
  arrivalHHMM: string;
  onArrival: (v: string) => void;
  departureHHMM: string;
  onDeparture: (v: string) => void;
}) {
  const toMins = (hhmm: string): number | null => {
    const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const a = toMins(arrivalHHMM);
  const d = toMins(departureHHMM);
  const preview = a != null && d != null && d > a ? formatDurationHm(d - a) : "-";
  return (
    <div className="mt-5">
      <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Time on site</h3>
      <div className="grid gap-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="mb-1 block text-ele-body">Visit date</span>
          <input type="date" value={visitDateYmd} onChange={(e) => onVisitDate(e.target.value)} className={editInputCls} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ele-body">Arrived</span>
          <input type="time" value={arrivalHHMM} onChange={(e) => onArrival(e.target.value)} className={editInputCls} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ele-body">Left</span>
          <input type="time" value={departureHHMM} onChange={(e) => onDeparture(e.target.value)} className={editInputCls} />
        </label>
      </div>
      <p className="mt-1 text-xs text-ele-body opacity-70">
        Duration: {preview}. Leave a time blank if it was not recorded. Corrections are logged in the sheet history.
      </p>
    </div>
  );
}

// A call-out (reactive) sheet: the reported fault, what the engineer found and did,
// the lift status on leaving, the on-site times and the calculated labour (so the
// Director can verify the time and charge before approving the Xero draft).
function CalloutSummary({ sheet, omitFaultReported }: { sheet: JobSheetListItem; omitFaultReported?: boolean }) {
  return (
    <div>
      <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Call-out report</h3>
      <dl className="space-y-2 text-sm">
        {!omitFaultReported && <Detail label="Fault reported" value={sheet.faultReported} />}
        <Detail label="Fault found" value={sheet.faultFound} />
        <Detail label="Work carried out" value={sheet.workCarriedOut} />
        <Detail label="Lift status on leaving" value={sheet.liftStatusOnLeaving} />
      </dl>
      <CalloutTimes sheet={sheet} />
      {sheet.labourCharge != null && <CalloutLabourNote charge={sheet.labourCharge} minutes={sheet.labourMinutes} />}
    </div>
  );
}

function CalloutTimes({ sheet }: { sheet: JobSheetListItem }) {
  return (
    <div className="mt-3 grid grid-cols-3 gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm">
      <div>
        <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Arrived</div>
        <div className="text-ele-strong">{formatTime(sheet.arrivalTime)}</div>
      </div>
      <div>
        <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Left</div>
        <div className="text-ele-strong">{formatTime(sheet.departureTime)}</div>
      </div>
      <div>
        <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Time on site</div>
        <div className="text-ele-strong">{fmtMinutes(sheet.labourMinutes)}</div>
      </div>
    </div>
  );
}

// The labour note, worded for the actual case so it never over-claims a Xero change:
//   - £0 (bundled Complete) -> "bundled, no separate charge"
//   - an hour or less -> just the first-hour minimum (already on the call-out invoice)
//   - over an hour -> the additional time was added to the call-out's draft to confirm
function CalloutLabourNote({ charge, minutes }: { charge: number; minutes: number | null }) {
  if (charge === 0) {
    return <p className="mt-2 text-sm text-ele-body">Bundled in contract: no separate labour charge.</p>;
  }
  const hasAdditional = minutes != null && minutes > 60;
  return (
    <p className="mt-2 text-sm text-ele-body">
      Labour: <span className="text-ele-strong">{gbpExact(charge)}</span> (ex VAT).{" "}
      {hasAdditional
        ? "The first hour is on the call-out invoice; the additional time has been added to the call-out's Xero draft for the office to confirm."
        : "This is the first-hour minimum, already on the call-out invoice."}
    </p>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">{label}</dt>
      <dd className="text-ele-strong">{value?.trim() ? value : <span className="text-ele-body opacity-60">Not recorded</span>}</dd>
    </div>
  );
}

// Parent back-link header (Session ADH): the lift, site and customer this record belongs to, each a
// link to its detail page. Shown at the top of the panel so a record reached from the Service
// Records list or a deep-link can jump back to its parents (the audit's top Section B4 gap). Each
// link renders only when its id is present, so a free-text emergency call-out with no lift shows
// only what it has (or nothing at all). The lift ref uses the blue reference treatment; site and
// customer use the green name-link treatment, matching OutstandingJobsPanel.
function RecordParentHeader({ sheet }: { sheet: JobSheetListItem }) {
  const [addLoler, setAddLoler] = useState(false);
  const links: ReactNode[] = [];
  if (sheet.assetId) {
    links.push(
      <Link key="lift" to={`/office/lifts/${encodeURIComponent(sheet.assetId)}`} className="ele-ref underline-offset-2 hover:underline">
        {sheet.ref}
      </Link>,
    );
  }
  if (sheet.siteId) {
    links.push(
      <Link key="site" to={`/office/sites/${encodeURIComponent(sheet.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
        {sheet.siteName}
      </Link>,
    );
  }
  if (sheet.customerId && sheet.customerName) {
    links.push(
      <Link key="customer" to={`/office/customers/${encodeURIComponent(sheet.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
        {sheet.customerName}
      </Link>,
    );
  }
  if (links.length === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      {links.map((link, i) => (
        <span key={i} className="inline-flex items-center gap-x-2">
          {i > 0 && (
            <span aria-hidden className="text-ele-body opacity-50">
              |
            </span>
          )}
          {link}
        </span>
      ))}
      {/* Session BAD: an examination report that arrived by email or as a file goes straight onto this visit's lift. */}
      {sheet.assetId ? (
        <>
          <span aria-hidden className="text-ele-body opacity-50">
            |
          </span>
          <button type="button" onClick={() => setAddLoler(true)} className="font-menu text-xs tracking-wide text-ele-blue hover:underline">
            Add LOLER report
          </button>
          {addLoler ? <AddLolerReportModal assetId={sheet.assetId} liftLabel={sheet.ref} onClose={() => setAddLoler(false)} onDone={() => setAddLoler(false)} /> : null}
        </>
      ) : null}
    </div>
  );
}

// The state chip for one inspection area in the office read view (Session ADM Loop 3). Mirrors
// the engineer/customer reading of the same fourteen areas: a clean area (something recorded, no
// fail or advisory) reads green "Pass"; an area with a fail or advisory reads amber and lists the
// counts; an area with nothing recorded reads muted "Not recorded". Pure.
function areaSummary(g: StoredAreaGroup<JobSheetChecklistEntry>): { label: string; cls: string } {
  if (g.entries.length === 0) return { label: "Not recorded", cls: "text-ele-body opacity-60" };
  if (g.fails > 0 || g.advisories > 0) {
    const label =
      g.fails > 0 && g.advisories > 0
        ? `${g.fails} failed, ${g.advisories} advisory`
        : g.fails > 0
          ? `${g.fails} failed`
          : `${g.advisories} advisory`;
    return { label, cls: "text-amber-400" };
  }
  return { label: "Pass", cls: "text-ele-green" };
}

// The office read view of a stored checklist (Session ADM Loop 3). For the ELE-MS-001 passenger
// checklist it presents the SAME fourteen inspection areas the engineer confirms and the customer
// document shows: one row per area with a state chip, each row expanding to its recorded items
// (item text, result and note). Areas expand independently. A non-passenger checklist (goods /
// platform / equipment / legacy) has no fourteen-area mapping, so it keeps the flat item list
// exactly as before. Read-only; editing keeps its own per-item form.
function ChecklistAreaView({ entries }: { entries: JobSheetChecklistEntry[] }) {
  const groups = groupStoredChecklistByArea(entries);
  const [openAreas, setOpenAreas] = useState<Record<string, boolean>>({});
  if (!groups) {
    // Goods / platform / equipment / legacy: the flat list, unchanged.
    return <FlatChecklistList entries={entries} />;
  }
  return (
    <ul className="space-y-1.5">
      {groups.map((g) => {
        // Collapsed by default (Director decision, Session ADM): the office reads fourteen area
        // rows at a glance, not ninety-odd items; a row expands on demand to its recorded entries.
        const open = openAreas[g.key] === true;
        const chip = areaSummary(g);
        return (
          <li key={g.key} className="rounded-ele border border-ele-purple-accent bg-ele-surface">
            <button
              type="button"
              onClick={() => setOpenAreas((o) => ({ ...o, [g.key]: !open }))}
              aria-expanded={open}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left max-sm:min-h-11"
            >
              <span className="flex min-w-0 items-center gap-2 text-sm text-ele-strong">
                <svg aria-hidden viewBox="0 0 16 16" className={`h-3 w-3 shrink-0 fill-current text-ele-body transition-transform ${open ? "rotate-90" : ""}`}>
                  <path d="M6 4l4 4-4 4z" />
                </svg>
                <span className="truncate">{g.label}</span>
              </span>
              <span className={`shrink-0 font-menu text-[10px] uppercase tracking-wide ${chip.cls}`}>{chip.label}</span>
            </button>
            {open && (
              <div className="border-t border-ele-purple-accent px-3 py-2">
                {g.entries.length === 0 ? (
                  <p className="text-sm text-ele-body opacity-70">Nothing recorded for this area on this visit.</p>
                ) : (
                  <FlatChecklistList entries={g.entries} />
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// The flat stored-checklist list (item text + result + note). The office read view for a
// non-passenger checklist and the expanded body of each passenger area. Unchanged rendering from
// the pre-Loop-3 flat list.
function FlatChecklistList({ entries }: { entries: JobSheetChecklistEntry[] }) {
  return (
    <ul className="space-y-1 text-sm">
      {entries.map((c, idx) => (
        <li key={c.id ?? idx} className="flex justify-between gap-3">
          <span className="text-ele-strong">{c.item || "Item"}</span>
          <span className={`font-menu text-xs uppercase tracking-wide ${checkWordClass(c.status)}`}>
            {CHECK_LABEL[c.status] ?? c.status}
            {c.note ? ` (${c.note})` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---- The More menu (Session ADM Loop 3: one action grammar for the record panel) ----
// Secondary and destructive actions valid for the record's CURRENT state live here, so the panel
// foot keeps exactly one primary and at most two quiet secondaries. Local to this panel (the
// shared OverflowMenu deliberately stays open after a click for its two-step ConfirmButton
// children; this menu must close on selection). Closes on Escape, outside click and after
// selecting an item; items meet the 44px tap floor on phones. No coloured left borders anywhere
// (house rule): tone is text colour + hover wash only.

export interface MoreActionItem {
  key: string;
  label: string;
  tone?: "amber" | "danger";
  // A link item (PDF download / Drive file) opens in a new tab; an action item runs onSelect.
  href?: string;
  onSelect?: () => void;
  // A send/resend item arms on the first click (ConfirmButton) and only fires + closes the menu
  // on the second, matching the Approve path's two-step confirm before an email goes out.
  confirm?: boolean;
}

const moreItemBase =
  "w-full rounded-ele border border-transparent px-3 py-1.5 text-left font-menu text-xs tracking-wide transition max-sm:min-h-11 max-sm:flex max-sm:items-center";
const MORE_ITEM_VARIANT: Record<"default" | "amber" | "danger", string> = {
  default: "text-ele-body hover:text-ele-strong",
  amber: "text-amber-400 hover:bg-amber-500/10",
  danger: "text-ele-danger hover:bg-red-500/10",
};

export function MoreActionsMenu({ items, ariaLabel = "More actions for this record" }: { items: MoreActionItem[]; ariaLabel?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (items.length === 0) return null;
  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
      >
        More
      </button>
      {open && (
        <div
          role="menu"
          aria-label={ariaLabel}
          className="absolute left-0 z-30 mt-1 flex min-w-52 flex-col gap-1 rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] p-2 shadow-lg"
        >
          {items.map((it) =>
            it.href ? (
              <a
                key={it.key}
                href={it.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className={`${moreItemBase} ${MORE_ITEM_VARIANT[it.tone ?? "default"]}`}
              >
                {it.label}
              </a>
            ) : it.confirm ? (
              <ConfirmButton
                key={it.key}
                onConfirm={() => {
                  setOpen(false);
                  it.onSelect?.();
                }}
                label={it.label}
                className={`${moreItemBase} ${MORE_ITEM_VARIANT[it.tone ?? "default"]}`}
              />
            ) : (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  setOpen(false);
                  it.onSelect?.();
                }}
                className={`${moreItemBase} ${MORE_ITEM_VARIANT[it.tone ?? "default"]}`}
              >
                {it.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

// ---- The panel ----

export function ServiceRecordPanel({
  sheet,
  onSent,
  onApproved,
  onChanged,
  onAmended,
  onDirtyChange,
  omitFaultReported,
}: {
  // Session BCS: the call-out page already states the reported fault in its Summary; it sets this
  // when the sheet's copy says the same, so the fault shows once.
  omitFaultReported?: boolean;
  sheet: JobSheetListItem;
  // Send to Customer stamped: update the caller's copy of the row so the sent state shows.
  // emailSentTo is the resolved recipient (Session AJD); null/undefined on an idempotent
  // already-sent reply, where the caller should leave its existing value alone.
  onSent: (jobId: string, sentAt: string, emailSentTo?: string | null) => void;
  // The office approval completed: flip the caller's copy to complete + emailed.
  onApproved: (jobId: string, res: { driveFileUrl: string | null; emailSentTo: string | null }) => void;
  // The record was archived/deleted/reactivated or returned to the engineer: reload the source.
  onChanged: () => void;
  // An amendment was saved: reload the record (keeping it open) so the audit trail refreshes.
  onAmended: (jobId: string) => void;
  // Unsaved-edit signal for the parent's collapse guard (route navigation and reload/close are
  // guarded inside the panel itself).
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const archived = sheet.archivedAt != null;
  const willArchive = sheetWillArchive(sheet);

  // ---- Send to Customer ----
  // Session BBH: the report send opens a recipients modal; sending is the modal being open for a fresh send.
  const [sendModal, setSendModal] = useState<null | "fresh" | "filed">(null);
  const sending = sendModal === "fresh";
  const [sendError, setSendError] = useState<string | null>(null);
  // Hides the amended-since-sent banner once the office has re-sent the corrected version
  // (the server-computed amendedSinceSent only refreshes on the next full reload).
  const [reSent, setReSent] = useState(false);

  // Local photo count so uploads/deletes reflect immediately without a full reload.
  const [photoCount, setPhotoCount] = useState(sheet.photoCount);
  useEffect(() => setPhotoCount(sheet.photoCount), [sheet.photoCount]);

  // ---- Archive / delete / reactivate ----
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState(false);

  // ---- Office approval (field loop) ----
  const [confirming, setConfirming] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approved, setApproved] = useState<{
    driveFileUrl: string | null;
    emailSentTo: string | null;
    draftNote?: string | null;
    invoiceNote?: string | null;
    timesheetNote?: string | null;
    // Session ARK (W-10): the open defects this job was raised to fix, offered for resolution.
    defectsToReview?: RemedialDefectOfferItem[];
  } | null>(null);
  // Session BBH: the report send opens a recipients modal (To and Cc chosen, every address named before it goes), and the
  // office can record a report it sent from Gmail by hand.
  const resending = sendModal === "filed";
  const [showSentOutside, setShowSentOutside] = useState(false);
  const [sentOutside, setSentOutside] = useState(sheet.sentOutside ?? null);
  const [outsideError, setOutsideError] = useState<string | null>(null);
  const [clearingOutside, setClearingOutside] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [showReturn, setShowReturn] = useState(false);
  // Session BDG2: reopen an approved job (admin only).
  const [showReopen, setShowReopen] = useState(false);
  const user = useOptionalAuthUser();
  const isAdmin = user?.role === "admin";
  const [returnReason, setReturnReason] = useState("");
  const [returning, setReturning] = useState(false);
  // Undo a backdated office entry before approval (Session AEB): the visit returns to
  // scheduled and the entered sheet is removed. Two-step via the shared ConfirmDeleteModal.
  const [showUndoBackdated, setShowUndoBackdated] = useState(false);
  const [undoingBackdated, setUndoingBackdated] = useState(false);
  const [undoBackdatedError, setUndoBackdatedError] = useState<string | null>(null);

  // ---- Inline amendment editing (Session ADE: the retired AmendJobSheetModal, in place) ----
  const [editing, setEditing] = useState(false);
  const [baseline, setBaseline] = useState<AmendEditState | null>(null);
  const [checklist, setChecklist] = useState<JobSheetChecklistEntry[]>([]);
  const [defects, setDefects] = useState<DefectRow[]>([]);
  const [parts, setParts] = useState<PartRow[]>([]);
  const [advisoryNotes, setAdvisoryNotes] = useState("");
  const [engineerNotes, setEngineerNotes] = useState("");
  const [faultFound, setFaultFound] = useState("");
  const [workCarriedOut, setWorkCarriedOut] = useState("");
  const [liftStatus, setLiftStatus] = useState("");
  // Visit times + date (Session ADJ): office-editable so a visit the engineer did not stamp
  // still shows real times on the customer document. Blank clears a time recorded in error.
  const [visitDateYmd, setVisitDateYmd] = useState("");
  const [arrivalHHMM, setArrivalHHMM] = useState("");
  const [departureHHMM, setDepartureHHMM] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [invoiceReviewNote, setInvoiceReviewNote] = useState(false);
  // Set when a corrected arrival/departure could not reach the TimesheetEntry approval already
  // wrote (the same two real-loss reasons approve() can report). Session AKY.
  const [amendTimesheetNote, setAmendTimesheetNote] = useState<string | null>(null);
  // Office-assisted checklist completion (Session ADJ): seed the standard checklist onto a sheet
  // that has none, so the office can record what the engineer confirms by phone. Every seeded row
  // starts N/A (nothing is claimed until the office sets it), and the save routes through the
  // audited amendment path like any other edit.
  const [seeding, setSeeding] = useState(false);
  const [seededTemplateName, setSeededTemplateName] = useState<string | null>(null);
  // The fourteen-area checklist editor (Session ADO): active for a passenger ELE-MS-001 checklist,
  // whether amending an existing record (pre-populated) or recording a new one (seeded). The flat
  // `checklist` state above stays the editor for a non-passenger / legacy checklist. areaSource is
  // the ordered item set; areaStatuses holds only the flagged exceptions; areaConfirmed is the
  // affirmative per-area record; areaItemNotes the per-item notes. effectiveChecklist (below) is
  // derived from these for the dirty check and the save body, so the amend endpoint is unchanged.
  const [areaMode, setAreaMode] = useState(false);
  const [areaSource, setAreaSource] = useState<OfficeAreaSourceItem[]>([]);
  const [areaStatuses, setAreaStatuses] = useState<Record<string, ChecklistStatus>>({});
  const [areaConfirmed, setAreaConfirmed] = useState<Record<string, boolean>>({});
  const [areaItemNotes, setAreaItemNotes] = useState<Record<string, string>>({});

  const areaItems = useMemo(() => officeAreaItems(areaSource), [areaSource]);
  const areas = useMemo(() => buildAreaGroups(areaItems, []), [areaItems]);

  // Mirror the engineer's setItemStatus (JobDetail): clearing an exception (null) removes it so the
  // item records as a Pass when its area is confirmed; N/A or clearing also drops any stale note.
  function setAreaItemStatus(id: string, status: ChecklistStatus | null) {
    setAreaStatuses((s) => {
      const c = { ...s };
      if (status == null) delete c[id];
      else c[id] = status;
      return c;
    });
    if (status == null || status === "na") {
      setAreaItemNotes((n) => {
        if (!n[id]) return n;
        const c = { ...n };
        delete c[id];
        return c;
      });
    }
    if (editError) setEditError(null);
  }

  async function seedChecklistFromTemplate() {
    if (!sheet.jobId || seeding) return;
    setSeeding(true);
    setEditError(null);
    try {
      const template = await api.jobsheets.checklistTemplate(sheet.jobId);
      setSeededTemplateName(template.name);
      if (checklistUsesAreaEditor(template.items)) {
        // A passenger checklist: record it through the fourteen-area UI. Nothing is confirmed at
        // the start, so every item derives to N/A until the office confirms an area (or all areas)
        // -- one tap for a clean office-assisted record, never a ninety-odd-row scroll.
        setAreaMode(true);
        setAreaSource(template.items.map((i) => ({ id: i.id, item: i.item, ...(i.category ? { category: i.category } : {}) })));
        setAreaStatuses({});
        setAreaConfirmed({});
        setAreaItemNotes({});
        setChecklist([]);
      } else {
        // Equipment (ELE-MS-002) / goods / legacy: keep the flat editor, all N/A until set.
        setAreaMode(false);
        setAreaSource([]);
        setChecklist(
          template.items.map((i) => ({
            id: i.id,
            item: i.item,
            status: "na",
            ...(i.category ? { category: i.category } : {}),
          })),
        );
      }
    } catch (err) {
      setEditError(
        err instanceof ApiError && (err.status === 404 || err.status === 409)
          ? err.message || "No standard checklist applies to this job."
          : "Could not load the standard checklist. Please try again.",
      );
    } finally {
      setSeeding(false);
    }
  }

  // The checklist that will be saved: derived from the fourteen-area editor when active, else the
  // flat editor's rows. The dirty check and the save body both read this, so the two editors save
  // through the one unchanged amend path.
  const effectiveChecklist = areaMode
    ? deriveOfficeChecklist(areaSource, areas, areaStatuses, areaConfirmed, areaItemNotes)
    : checklist;

  const edited: AmendEditState = {
    checklist: effectiveChecklist,
    defects: defects.map((d) => d.text),
    parts,
    advisoryNotes,
    engineerNotes,
    faultFound,
    workCarriedOut,
    liftStatusOnLeaving: liftStatus,
    visitDateYmd,
    arrivalHHMM,
    departureHHMM,
  };
  const dirty = editing && baseline != null && amendStateDirty(baseline, edited, sheet.isCallout);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  // Never leave the parent thinking the panel is dirty after it unmounts.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Reload/close protection while an edit is unsaved (route navigation is guarded by the
  // NavigationBlocker below).
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  function startEdit() {
    setBaseline(baselineFromSheet(sheet));
    // A passenger ELE-MS-001 checklist is amended through the fourteen-area editor, pre-populated
    // from the stored results (correcting one item is drill-into-its-area, never a scroll of the
    // whole checklist). A non-passenger / legacy checklist keeps the flat editor.
    if (!sheet.isCallout && checklistUsesAreaEditor(sheet.checklist)) {
      const init = initOfficeAreaState(sheet.checklist);
      setAreaMode(true);
      setAreaSource(sheet.checklist.map((c) => ({ id: c.id ?? "", item: c.item, ...(c.category ? { category: c.category } : {}) })));
      setAreaStatuses(init.statuses);
      setAreaConfirmed(init.confirmed);
      setAreaItemNotes(init.notes);
      setChecklist([]);
    } else {
      setAreaMode(false);
      setAreaSource([]);
      setChecklist(sheet.checklist.map((c) => ({ ...c })));
    }
    setSeededTemplateName(null);
    setDefects(sheet.defects.map((d) => ({ _k: nextKey(), text: d })));
    setParts(sheet.partsUsed.map((p) => ({ ...p, _k: nextKey() })));
    setAdvisoryNotes(sheet.advisoryNotes ?? "");
    setEngineerNotes(sheet.engineerNotes ?? "");
    setFaultFound(sheet.faultFound ?? "");
    setWorkCarriedOut(sheet.workCarriedOut ?? "");
    setLiftStatus(sheet.liftStatusOnLeaving ?? "");
    setVisitDateYmd(isoToLocalYmd(sheet.completedAt));
    setArrivalHHMM(isoToLocalHHMM(sheet.arrivalTime));
    setDepartureHHMM(isoToLocalHHMM(sheet.departureTime));
    setReason("");
    setEditError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setEditError(null);
  }

  function setChecklistAt(index: number, patch: Partial<JobSheetChecklistEntry>) {
    setChecklist((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  async function saveAmendment() {
    if (!sheet.jobId || saving) return;
    if (areaMode) {
      // The honesty gate, mirroring the engineer F1 fix: nothing is saved unless every applicable
      // inspection area has been affirmatively confirmed. This is what stops a stray "Undo" (which
      // clears the pre-populated confirmations) from silently rewriting a recorded passing checklist
      // to all-N/A on save: the office must deliberately re-confirm the areas first.
      if (!allAreasConfirmed(areas, areaConfirmed)) {
        setEditError(
          "Confirm every inspection area before saving. Use 'Confirm all areas checked and passed', or open an area to record what you found.",
        );
        return;
      }
      // A Fail the office records this session must describe the fault (a pre-existing note-less
      // Fail on a legacy record is grandfathered, so an unrelated amendment is not blocked).
      const missing = firstUnnotedNewFail(areaSource, areaStatuses, areaItemNotes, sheet.checklist);
      if (missing) {
        setEditError(`Add a note describing the fault for "${missing.item}" before saving.`);
        return;
      }
    }
    // Compose the visit time/date fields BEFORE anything is sent, so a typo (departure before
    // arrival, a cleared date) is caught client-side with a clear message.
    const times = baseline ? buildTimeAmendment(baseline, edited, { arrivalTime: sheet.arrivalTime, departureTime: sheet.departureTime }) : { fields: {} };
    if (times.error) {
      setEditError(times.error);
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      // Send only the sections relevant to the sheet type. The server diffs against the stored
      // sheet, records exactly what changed, and rejects a no-op (400 no_changes).
      const body: Parameters<typeof api.jobsheets.amend>[1] = {
        ...(times.fields ?? {}),
        defects: defects.map((d) => d.text.trim()).filter((t) => t.length > 0),
        partsUsed: parts
          .filter((p) => p.description.trim().length > 0)
          .map((p) => ({
            ...(p.partCode?.trim() ? { partCode: p.partCode.trim() } : {}),
            description: p.description.trim(),
            quantity: Math.max(1, Math.round(p.quantity || 1)),
            ...(typeof p.unitCost === "number" && Number.isFinite(p.unitCost) ? { unitCost: p.unitCost } : {}),
          })),
        engineerNotes: engineerNotes.trim() || null,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      };
      if (sheet.isCallout) {
        body.faultFound = faultFound.trim() || null;
        body.workCarriedOut = workCarriedOut.trim() || null;
        body.liftStatusOnLeaving = liftStatus.trim() || null;
      } else {
        body.checklist = effectiveChecklist.map((c) => ({
          ...(c.id ? { id: c.id } : {}),
          item: c.item,
          status: c.status,
          ...(c.category ? { category: c.category } : {}),
          ...(c.note?.trim() ? { note: c.note.trim() } : {}),
        }));
        body.advisoryNotes = advisoryNotes.trim() || null;
      }
      const res = await api.jobsheets.amend(sheet.jobId, body);
      setEditing(false);
      setInvoiceReviewNote(res.invoiceReviewNeeded);
      setAmendTimesheetNote(res.timesheetNote ?? null);
      onAmended(sheet.jobId);
    } catch (err) {
      setEditError(
        err instanceof ApiError && err.status === 400
          ? "There are no changes to save."
          : err instanceof ApiError
            ? err.message || err.code
            : "Could not save the amendment. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  // ---- Actions ----

  // Session BBH: Send opens the recipients modal; the modal does the confirmed send and reports back here.
  function sendToCustomer() {
    if (!sheet.jobId || sending) return;
    setSendError(null);
    setSendModal("fresh");
  }

  async function clearSentOutside() {
    if (!sheet.jobId) return;
    setClearingOutside(true);
    setOutsideError(null);
    try {
      await api.jobsheets.clearSentOutside(sheet.jobId);
      setSentOutside(null);
      onChanged();
    } catch (err) {
      setOutsideError(friendlyApiError(err, "The record could not be cleared. Please try again."));
    } finally {
      setClearingOutside(false);
    }
  }

  async function confirmDelete() {
    if (!sheet.jobId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.jobsheets.remove(sheet.jobId);
      setShowDelete(false);
      onChanged();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the record. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  async function reactivate() {
    if (!sheet.jobId) return;
    setReactivating(true);
    try {
      await api.jobsheets.reactivate(sheet.jobId);
      onChanged();
    } catch {
      setSendError("Could not reactivate the record. Please try again.");
    } finally {
      setReactivating(false);
    }
  }

  const isPending = sheet.jobStatus === "pending_approval";
  const wasApproved = approved != null || (sheet.jobStatus === "complete" && sheet.emailSentAt != null);
  const filedUrl = approved?.driveFileUrl ?? sheet.driveFileUrl;
  const sentTo = approved?.emailSentTo ?? sheet.emailSentTo;

  // Whether the customer has actually RECEIVED this record, as distinct from wasApproved. Job.
  // emailSentAt is stamped on EVERY approval (service since Session AGX, call-out since AHY),
  // whether or not anything was sent, so wasApproved alone cannot tell "approved" from "delivered"
  // -- it is true the instant a job is approved. sentToClientAt (POST .../send-to-client) and
  // emailSentTo (POST .../resend, and now also send-to-client -- Session AJD) are the two real
  // send stamps; either one means a genuine send happened. This mirrors the exact two-signal check
  // jobsheets.ts's computeAmendedSinceSent already makes server-side. deliveredAt prefers
  // sentToClientAt (the fresher, JobSheet-native stamp) and falls back to emailSentAt only when
  // the record was delivered by resend alone, matching that same helper's Math.max ordering.
  const delivered = sheet.sentToClientAt != null || sentTo != null;
  const deliveredAt = sheet.sentToClientAt ?? (sentTo ? sheet.emailSentAt : null);
  const deliveredTo = sentTo;
  // Session BEG (the release gate): the primary slot offers Send now only for a RELEASED record (wasApproved: complete with
  // the approval stamp). A manual/backdated record (POST /jobsheets/manual) is created already complete with no emailSentAt at
  // all (it never runs the approve() route), so the send route refuses it ("This report has not been released yet") and the
  // slot offers nothing for it, rather than a button that can only fail. Before Session BEG it was offered on request.

  async function approve() {
    if (!sheet.jobId || approving) return;
    setApproving(true);
    setApproveError(null);
    try {
      const res = await api.jobsheets.approve(sheet.jobId);
      setApproved({
        driveFileUrl: res.driveFileUrl,
        emailSentTo: res.emailSentTo,
        draftNote: res.draftNote ?? null,
        invoiceNote: res.invoiceNote ?? null,
        timesheetNote: res.timesheetNote ?? null,
        defectsToReview: res.defectsToReview ?? [],
      });
      setConfirming(false);
      onApproved(sheet.jobId, { driveFileUrl: res.driveFileUrl, emailSentTo: res.emailSentTo });
    } catch (err) {
      // The server sends a per-step human message; ApiError.code carries it.
      setApproveError(friendlyApiError(err, "Could not approve the job. Please try again."));
    } finally {
      setApproving(false);
    }
  }

  // Session BBH: the filed-PDF send opens the same recipients modal.
  function resend() {
    if (!sheet.jobId || resending) return;
    setResendMsg(null);
    setSendModal("filed");
  }

  async function returnToEngineer() {
    if (!sheet.jobId || returning || !returnReason.trim()) return;
    setReturning(true);
    setApproveError(null);
    try {
      await api.jobsheets.returnToEngineer(sheet.jobId, returnReason.trim());
      setShowReturn(false);
      setReturnReason("");
      onChanged(); // the job leaves pending_approval (back to the engineer)
    } catch (err) {
      setApproveError(friendlyApiError(err, "Could not return the job. Please try again."));
    } finally {
      setReturning(false);
    }
  }

  // The More menu's contents (Session ADM Loop 3), filtered HARD to the actions valid for this
  // record's current state: never Resend on an unapproved record, never Return on a non-pending
  // one, never Archive/Delete on an archived one. The invoice link/unlink keeps its one home in
  // the body's Invoice section, so it is deliberately not duplicated here.
  const moreActions: MoreActionItem[] = [];
  if (sheet.jobId) {
    if (!sheet.isCallout && sheet.checklist.length > 0) {
      // Office-only full itemised checklist (Session ADJ): the customer receives the short
      // summary; the full detail stays available here and can be provided on request.
      moreActions.push({ key: "full-checklist", label: "Full checklist (office copy)", href: api.jobsheets.fullPdfUrl(sheet.jobId) });
    }
    if (wasApproved && filedUrl) {
      moreActions.push({ key: "filed-pdf", label: "View filed PDF", href: filedUrl });
    }
    if (wasApproved) {
      // "Resend" only when something genuinely went (a call-out, or a service record already sent
      // from here). Since Session AGX an approved SERVICE record has only been DRAFTED, so this is
      // the office's SEND route, not a re-send, and calling it one would imply the customer already
      // holds the certificate. sentTo is non-null only after a real send.
      moreActions.push({ key: "resend", label: sentTo ? "Resend email" : "Send email now", onSelect: resend });
      // Session BBH: record a report the office sent from Gmail by hand (sends nothing).
      if (!sentOutside) moreActions.push({ key: "sent-outside", label: "Mark as sent outside Guvnor", onSelect: () => setShowSentOutside(true) });
      // Session BDG2: back to Closed, awaiting release, to correct it. Admin only; the modal shows the server's preview.
      if (isAdmin) moreActions.push({ key: "reopen-approval", label: "Reopen approval", tone: "amber", onSelect: () => setShowReopen(true) });
    }
    if (isPending && !wasApproved && !isBackdatedOfficeEntry(sheet)) {
      moreActions.push({
        key: "return",
        label: "Return to engineer",
        tone: "amber",
        onSelect: () => {
          setApproveError(null);
          setShowReturn(true);
        },
      });
    }
    // A backdated office entry has no engineer to return to (returning would drop a months-old
    // visit onto the PWA's active list). Its pre-approval undo is the revert (Session AEB).
    if (isPending && !wasApproved && isBackdatedOfficeEntry(sheet)) {
      moreActions.push({
        key: "undo-backdated",
        label: "Undo backdated entry",
        tone: "danger",
        onSelect: () => {
          setUndoBackdatedError(null);
          setShowUndoBackdated(true);
        },
      });
    }
    if (!archived) {
      moreActions.push({
        key: "delete",
        label: willArchive ? "Archive record" : "Delete record",
        tone: willArchive ? "amber" : "danger",
        onSelect: () => {
          setDeleteError(null);
          setShowDelete(true);
        },
      });
    }
  }

  return (
    <div>
      <NavigationBlocker when={dirty} />
      {showReopen && sheet.jobId && (
        <ReopenApprovalModal
          jobId={sheet.jobId}
          onClose={() => setShowReopen(false)}
          onReopened={() => {
            setShowReopen(false);
            // The record is no longer approved: drop the local approval so the panel stops offering approved actions.
            setApproved(null);
            onChanged();
          }}
        />
      )}

      {/* Parent back-links (Session ADH): jump from this record to its lift, site and customer. */}
      <RecordParentHeader sheet={sheet} />

      {/* Session AEB: the visible provenance for an office-entered retrospective completion.
          An OFFICE fact - the customer certificate carries no marking and no signature block. */}
      {isBackdatedOfficeEntry(sheet) && (
        <div className="mb-4 rounded-ele border border-ele-blue bg-ele-surface p-3">
          <p className="font-menu text-xs uppercase tracking-wide text-ele-blue">Office-entered retrospective completion</p>
          <p className="mt-1 text-sm text-ele-body">
            This record was entered by the office after the visit, dated the day the work was actually done. No customer
            signature exists for it and none appears on the certificate. Approving sends the certificate to the customer
            as normal.
          </p>
        </div>
      )}

      {/* Session ASW (D10): a completed service or repair with no risk assessment on file, flagged where the office
          will see it, with the control to add one. */}
      <MissingRiskAssessmentBanner sheet={sheet} onSaved={() => sheet.jobId && onAmended(sheet.jobId)} />

      {/* Session ACU: a care-equipment visit that left the equipment OUT OF SERVICE (the
          ELE-MS-002 service-state item failed) surfaces the Category A out-of-service state
          prominently, right where the office approves the visit. */}
      {sheet.equipmentOutOfService && (
        <div role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-danger/10 p-3">
          <p className="font-heading text-sm uppercase tracking-wide text-ele-danger">Equipment taken out of service</p>
          <p className="mt-1 text-sm text-ele-strong">
            The engineer left this equipment OUT OF SERVICE at the visit. This is a Category A situation: the equipment must not
            be used until the fault is put right and it is confirmed safe. On approval an immediate defect is raised and a client
            out-of-service notification is drafted to Gmail for your review.
          </p>
        </div>
      )}

      {/* Session AJW Item 2: the send/approve action area moved here from the bottom of the panel
          (display/navigation only -- no logic below changed). It used to sit after the whole record
          body (checklist/call-out summary, defects, parts, notes, time on site, risk assessment,
          profitability, signature, invoice link, photos), so reaching "Send now" or the PDF meant
          scrolling past all of it first. It is not behind a menu today (AJD already got that right),
          but it was not the first thing visible either, which is what this closes. */}
      {/* Amended-since-sent prompt (Session ABD): the record was corrected after it was
          delivered, so offer to re-send the corrected version. sendToCustomer regenerates the
          current sheet fresh, so the customer receives the corrected document. */}
      {sheet.amendedSinceSent && !reSent && (
        <div className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 p-3">
          <p className="text-sm text-amber-300">
            This record has been amended since it was last sent to the customer. Re-send the corrected version so their copy matches.
          </p>
          <Button
            size="sm"
            onClick={sendToCustomer}
            disabled={sending}
            className="mt-2"
          >
            {sending ? "Sending..." : "Re-send corrected version"}
          </Button>
        </div>
      )}

      {/* Session BAR: an engineer can now close a job on a later day than the visit. Before approving,
          the office sees the day the visit is recorded on and, when the sheet came in on a later day,
          how many days later. Information only: it says nothing about the engineer. */}
      {isPending && !wasApproved && sheet.completedAt && (
        <p className="mb-3 text-sm text-ele-body" data-testid="visit-date-info">
          Visit on {formatDate(sheet.completedAt)}.
          {(() => {
            const days = daysBetweenLocalDates(sheet.completedAt, sheet.receivedAt ?? null);
            return days > 0 ? ` Sent in ${days} ${days === 1 ? "day" : "days"} after the visit.` : "";
          })()}
          {/* Session BBC: more than a week after the visit, the engineer had to say why. The flag reads the
              same two dates as the line before it; the reason is the engineer's own, from the submission. */}
          {lateSubmissionDays(sheet.completedAt, sheet.receivedAt ?? null) != null && (
            <>
              {" "}
              <StatusPill word="Late" tone="amber" />
              <span data-testid="late-reason">
                {" "}
                {sheet.lateReason ? `Reason: ${sheet.lateReason}.` : "No reason recorded (sent from the phone before one was asked)."}
              </span>
            </>
          )}
        </p>
      )}

      {/* Session TV2: a technician visit awaiting approval - how time beyond its five hours is billed to the customer. */}
      {isPending && !wasApproved && sheet.jobId && sheet.jobType === "tech_visit" && (
        <TechVisitOverageControl key={`${sheet.jobId}:${sheet.techVisitOverageAsDay === true}`} jobId={sheet.jobId} furtherDay={sheet.techVisitOverageAsDay === true} onChanged={() => sheet.jobId && onAmended(sheet.jobId)} />
      )}

      {/* Session PN1: ONE quiet line saying whether the engineer was told when the job was assigned (push, text, or not told and why).
          Derived live from the audit trail; absent when the platform holds no record of a telling. Amber only when they were not. */}
      {sheet.engineerTold && (
        <p className={`mb-3 text-sm ${sheet.engineerTold.warn ? "text-amber-400" : "text-ele-body"}`} data-testid="engineer-told-line">
          {sheet.engineerTold.text}
        </p>
      )}

      {/* ---- Action area (Session ADM Loop 3): a hierarchy, not a wall of buttons. ONE
          state-computed primary action (solid green -- the single most important thing to do to
          this record right now), at most two quiet outline secondaries (Download + Amend), and
          everything else in the state-filtered More menu. ---- */}
      {sheet.jobId && (
        <div className="mb-5 flex flex-wrap items-center gap-3">
          {/* PRIMARY slot: exactly one solid-green action, or the record's status chip. */}
          {archived ? (
            <Button
              size="sm"
              onClick={reactivate}
              disabled={reactivating}
            >
              {reactivating ? "Reactivating..." : "Reactivate"}
            </Button>
          ) : isPending && !wasApproved ? (
            confirming ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span className="text-xs text-ele-body">
                  {/* Session BEG (the release gate): releasing a closed visit does nothing to the customer. It does not send,
                      does not draft an email and does not show the report on their portal; a person still presses Send email
                      now afterwards. The sentence says so, because the button used to promise a draft. */}
                  Releasing will generate the {sheet.isCallout ? "call-out report" : "service sheet"} and file it to Drive. Nothing goes to
                  the customer, and they cannot open it, until you press Send email now. Continue?
                </span>
                <Button
                  size="sm"
                  onClick={approve}
                  disabled={approving}
                >
                  {approving ? "Generating PDF..." : "Continue"}
                </Button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={approving}
                  className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50"
                >
                  Cancel
                </button>
              </span>
            ) : showReturn ? (
              <span className="inline-flex w-full flex-wrap items-center gap-2">
                <input
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Reason to return to the engineer"
                  className="min-w-48 flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
                />
                <button
                  type="button"
                  onClick={returnToEngineer}
                  disabled={returning || !returnReason.trim()}
                  className="rounded-ele border border-amber-500 px-3 py-2 font-menu text-xs tracking-wide text-amber-400 transition hover:bg-amber-500/10 disabled:opacity-50"
                >
                  {returning ? "Returning..." : "Return"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowReturn(false); setReturnReason(""); }}
                  disabled={returning}
                  className="rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50"
                >
                  Cancel
                </button>
              </span>
            ) : (
              <Button
                size="sm"
                onClick={() => { setApproveError(null); setConfirming(true); }}
              >
                {/* Session BEG: the office's approval of a closed visit IS the release, so the button says
                    so. It was "Approve and draft email" until then, when approval also drafted to the customer. */}
                Release for sending
              </Button>
            )
          ) : sheet.amendedSinceSent && !reSent ? (
            // The amended-since-sent banner above carries the "Re-send corrected version"
            // button (with its explanation), so the primary slot stays empty here: never two
            // identical send buttons at once.
            null
          ) : delivered ? (
            <StatusPill word={`Sent to ${deliveredTo ?? "the customer"} on ${formatDate(deliveredAt)}`} tone="green" />
          ) : wasApproved ? (
            // Session AJD: completed but genuinely never delivered to the customer -- the state that
            // left ELE-102-01 reading as done when nobody had ever pressed send. wasApproved alone
            // cannot detect this: since Session AGX/AHY, Job.emailSentAt is stamped on EVERY approval
            // whether or not anything was sent, so a check keyed on it is true the instant a job is
            // approved.
            //
            // The amber "Approved, not yet sent" badge is shown ONLY when wasApproved is true, i.e.
            // this record genuinely went through the real approve() workflow: that is the case worth
            // flagging as unfinished business, and it is what ELE-102-01, ELE-081 and ELE-083 all are
            // (Session AJD backfill check). A manual/backdated record (POST /jobsheets/manual) is
            // created already complete and never runs approve() at all -- wasApproved stays false for
            // it by construction -- so it is NOT a forgotten send and gets no urgency badge; the Send
            // now button still shows underneath, unchanged from the pre-existing (if accidental)
            // capability to send one of these on request, just without implying it was ever missed.
            <span className="inline-flex flex-wrap items-center gap-2">
              <StatusPill domain="releaseGate" status="ready_to_send" />
              {/* Session BBH: opens the recipients modal, whose own two-step confirm names every address. */}
              <Button size="sm" onClick={sendToCustomer}>
                Send now
              </Button>
            </span>
          ) : null}

          {/* SECONDARY: at most two quiet outline actions (green stays reserved for the primary). */}
          <a
            href={api.jobsheets.pdfUrl(sheet.jobId)}
            target="_blank"
            rel="noopener noreferrer"
            className="max-sm:min-h-11 inline-flex items-center gap-2 rounded-ele border border-ele-blue px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset"
          >
            Download Service Record PDF
          </a>
          {!archived && !editing && (
            <button
              type="button"
              onClick={startEdit}
              className="max-sm:min-h-11 inline-flex items-center gap-2 rounded-ele border border-ele-blue px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset"
            >
              Amend record
            </button>
          )}

          {/* Everything else, state-filtered (see moreActions above). */}
          <MoreActionsMenu items={moreActions} />

          {sendError && <span role="alert" className="text-xs text-ele-danger">{sendError}</span>}
          {/* Session BBH: a report a person recorded as sent outside Guvnor, with the way back. */}
          {sentOutside && (
            <span role="status" className="inline-flex w-full flex-wrap items-center gap-2 text-xs text-ele-body" data-testid="sent-outside">
              Sent outside Guvnor on {formatDate(sentOutside.sentAt)} to {[...sentOutside.to, ...sentOutside.cc].join(", ")}
              {sentOutside.recordedByName ? ` (recorded by ${sentOutside.recordedByName})` : ""}.{sentOutside.note ? ` ${sentOutside.note}` : ""}
              <ConfirmButton onConfirm={() => void clearSentOutside()} label="Clear" confirmLabel="Clear this record?" busy={clearingOutside} busyLabel="Clearing..." />
            </span>
          )}
          {outsideError && <span role="alert" className="text-xs text-ele-danger">{outsideError}</span>}
          {sendModal && sheet.jobId && (
            <ReportSendModal
              jobId={sheet.jobId}
              source={sendModal}
              onClose={() => setSendModal(null)}
              onSent={(res) => {
                if (sendModal === "fresh" && res.sentToClientAt) {
                  onSent(sheet.jobId!, res.sentToClientAt, res.to[0] ?? null);
                  setReSent(true); // clears the amended-since-sent banner without waiting for a reload
                } else {
                  setResendMsg(`Sent to ${[...res.to, ...res.cc].join(", ")}.`);
                }
              }}
            />
          )}
          {showSentOutside && sheet.jobId && (
            <SentOutsideModal
              jobId={sheet.jobId}
              suggestions={[sheet.emailSentTo].filter((x): x is string => !!x)}
              onClose={() => setShowSentOutside(false)}
              onSaved={() => onChanged()}
            />
          )}
          {approved && (
            // Session AGX: an approval sends nothing, so this must not claim it did. emailSentTo is only ever set by a
            // genuine send. Session BEG: it drafts nothing either, and the note says "Released" for that reason.
            <span role="status" className="w-full text-xs text-ele-green">
              {approved.emailSentTo ? `Job released. PDF sent to ${approved.emailSentTo}.` : "Job released."}
              {approved.draftNote ? ` ${approved.draftNote}` : ""}
            </span>
          )}
          {approved?.invoiceNote && (
            <span role="status" className="w-full text-xs text-ele-body">
              {approved.invoiceNote}
            </span>
          )}
          {approved?.timesheetNote && (
            <div role="alert" className="mt-1 w-full rounded-ele border border-amber-500 bg-amber-500/10 p-3">
              <p className="text-sm text-amber-300">{approved.timesheetNote}</p>
            </div>
          )}
          {/* Session ARK (W-10): an OFFER to resolve the defects this job was raised to fix, beside
              the other approval notes. Nothing is resolved unless the office presses Mark resolved,
              which is the defect page's own call (PATCH /api/defects/:id). */}
          {approved?.defectsToReview && approved.defectsToReview.length > 0 && <RemedialDefectOffer defects={approved.defectsToReview} />}
          {approveError && <span role="alert" className="w-full text-xs text-ele-danger">{approveError}</span>}
          {resendMsg && <span role="status" className="w-full text-xs text-ele-body">{resendMsg}</span>}
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          {sheet.isCallout ? (
            editing ? (
              <div>
                <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Call-out report</h3>
                {!omitFaultReported && (
                  <dl className="mb-3 space-y-2 text-sm">
                    <Detail label="Fault reported" value={sheet.faultReported} />
                  </dl>
                )}
                <div className="space-y-3">
                  <label className="block text-sm">
                    <span className="mb-1 block text-ele-body">Fault found</span>
                    <textarea rows={2} value={faultFound} onChange={(e) => setFaultFound(e.target.value)} className={editInputCls} />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-ele-body">Work carried out</span>
                    <textarea rows={2} value={workCarriedOut} onChange={(e) => setWorkCarriedOut(e.target.value)} className={editInputCls} />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-ele-body">Lift status on leaving</span>
                    <select value={liftStatus} onChange={(e) => setLiftStatus(e.target.value)} className={editInputCls}>
                      <option value="">Not recorded</option>
                      {LIFT_STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <EditableVisitTimes
                  visitDateYmd={visitDateYmd}
                  onVisitDate={setVisitDateYmd}
                  arrivalHHMM={arrivalHHMM}
                  onArrival={setArrivalHHMM}
                  departureHHMM={departureHHMM}
                  onDeparture={setDepartureHHMM}
                />
              </div>
            ) : (
              <CalloutSummary sheet={sheet} omitFaultReported={omitFaultReported} />
            )
          ) : (
            <>
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Checklist</h3>
              {sheet.checklist.length === 0 && !editing ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm text-ele-body opacity-70">No checklist recorded.</p>
                  {/* Record it now (Session ADM Loop 3, audit F4): open the amendment editor and
                      seed the standard checklist so the office can record what the engineer
                      confirms by phone. Every seeded row starts N/A and the save is an audited
                      office amendment (the honest office-assisted flow). */}
                  {!archived && sheet.jobId && (
                    <button
                      type="button"
                      onClick={() => { startEdit(); seedChecklistFromTemplate(); }}
                      disabled={seeding}
                      className="max-sm:min-h-11 rounded-ele border border-ele-blue px-3 py-1.5 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset disabled:opacity-50"
                    >
                      {seeding ? "Loading..." : "Record it now"}
                    </button>
                  )}
                </div>
              ) : editing ? (
                areaMode ? (
                  // Passenger ELE-MS-001 checklist: the fourteen-area recording UI (Session ADO),
                  // the same one the engineer uses. Confirm all areas in one action for a clean
                  // office-assisted record, or open an area to flag a specific item, instead of a
                  // scroll of the whole checklist. The result derives to the flat amend payload.
                  <>
                    {seededTemplateName && (
                      <p className="mb-2 text-xs text-ele-body opacity-70">
                        Recording the {seededTemplateName} checklist. Confirm all areas checked and passed, or open an area
                        to record a problem. The save is logged as an office amendment in the sheet history.
                      </p>
                    )}
                    <AreaChecklist
                      areas={areas}
                      confirmed={areaConfirmed}
                      statuses={areaStatuses}
                      notes={areaItemNotes}
                      onConfirmedChange={(next) => {
                        setAreaConfirmed(next);
                        if (editError) setEditError(null);
                      }}
                      onStatusChange={setAreaItemStatus}
                      onNoteChange={(id, v) => setAreaItemNotes((n) => ({ ...n, [id]: v }))}
                    />
                  </>
                ) : checklist.length === 0 ? (
                  <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
                    <p className="text-sm text-ele-body opacity-70">No checklist was recorded on this sheet.</p>
                    {/* Office-assisted completion (Session ADJ): record the engineer's
                        phone-confirmed results. Honest by construction: every row starts N/A,
                        and the save is logged as an office amendment, never an on-site entry. */}
                    <button
                      type="button"
                      onClick={seedChecklistFromTemplate}
                      disabled={seeding}
                      className="mt-2 rounded-ele border border-ele-blue px-3 py-1.5 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset disabled:opacity-50"
                    >
                      {seeding ? "Loading..." : "Record the standard checklist"}
                    </button>
                    <p className="mt-2 text-xs text-ele-body opacity-70">
                      For a visit the engineer completed on site but did not tick off in the app: confirm each item with the
                      engineer, set it here, and leave anything not confirmed as N/A. The save is recorded as an office
                      amendment in the sheet history.
                    </p>
                  </div>
                ) : (
                  <>
                  {seededTemplateName && (
                    <p className="mb-2 text-xs text-ele-body opacity-70">
                      Recording the {seededTemplateName} checklist. Set each item the engineer confirms; leave anything not
                      confirmed as N/A.
                    </p>
                  )}
                  <ul className="space-y-2">
                    {checklist.map((c, i) => (
                      <li key={c.id ?? `${c.item}-${i}`} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="min-w-0 flex-1 text-sm text-ele-strong">{c.item || "Item"}</span>
                          <select
                            aria-label={`Result for ${c.item || "item"}`}
                            value={c.status}
                            onChange={(e) => setChecklistAt(i, { status: e.target.value })}
                            className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-strong outline-none focus:border-ele-green"
                          >
                            {STATUS_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <input
                          value={c.note ?? ""}
                          onChange={(e) => setChecklistAt(i, { note: e.target.value })}
                          placeholder="Note (optional)"
                          aria-label={`Note for ${c.item || "item"}`}
                          className="mt-2 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-strong outline-none focus:border-ele-green"
                        />
                      </li>
                    ))}
                  </ul>
                  </>
                )
              ) : (
                // The office read view: the fourteen inspection areas for a passenger checklist
                // (the same areas the engineer confirms and the customer document shows), or the
                // flat item list for a non-passenger checklist (Session ADM Loop 3).
                <ChecklistAreaView entries={sheet.checklist} />
              )}
            </>
          )}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-heading text-sm uppercase tracking-wide text-ele-blue">Defects</h3>
            {editing && (
              <button type="button" onClick={() => setDefects((r) => [...r, { _k: nextKey(), text: "" }])} className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                Add defect
              </button>
            )}
          </div>
          {editing ? (
            defects.length === 0 ? (
              <p className="text-sm text-ele-body opacity-70">None recorded.</p>
            ) : (
              <ul className="space-y-2">
                {defects.map((d, i) => (
                  <li key={d._k} className="flex items-center gap-2">
                    {/* Session ASC (B-F3): a TEXTAREA sized to its content (one row per line, at least one),
                        never a single-line input. The manual record form takes a multi-line fault note (its
                        own field is a textarea, AddServiceRecordModal), and since B3 that note arrives here as
                        a row; an input strips line breaks from its value, so a one-character edit saved the
                        lines fused into one run-on string. Same ele-* styling as the input it replaces. */}
                    <textarea
                      value={d.text}
                      rows={Math.max(1, d.text.split("\n").length)}
                      onChange={(e) => setDefects((rows) => rows.map((r) => (r._k === d._k ? { ...r, text: e.target.value } : r)))}
                      placeholder="Describe the defect"
                      aria-label={`Defect ${i + 1}`}
                      className="min-w-0 flex-1 resize-y rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
                    />
                    <button type="button" onClick={() => setDefects((rows) => rows.filter((r) => r._k !== d._k))} aria-label={`Remove defect ${i + 1}`} className="rounded-ele border border-ele-danger px-2 py-1 text-xs text-ele-danger hover:bg-red-500/10">
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : sheet.defects.length === 0 ? (
            <p className="text-sm text-ele-body opacity-70">None reported.</p>
          ) : (
            <ul className="list-disc space-y-1 pl-5 text-sm text-ele-strong">
              {sheet.defects.map((d, idx) => (
                <li key={idx}>{d}</li>
              ))}
            </ul>
          )}

          <div className="mb-2 mt-4 flex items-center justify-between">
            <h3 className="font-heading text-sm uppercase tracking-wide text-ele-blue">Parts used</h3>
            {editing && (
              <button type="button" onClick={() => setParts((r) => [...r, { _k: nextKey(), description: "", quantity: 1 }])} className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                Add part
              </button>
            )}
          </div>
          {editing ? (
            parts.length === 0 ? (
              <p className="text-sm text-ele-body opacity-70">None recorded.</p>
            ) : (
              <ul className="space-y-2">
                {parts.map((p, i) => (
                  <li key={p._k} className="flex flex-wrap items-center gap-2">
                    <input
                      value={p.description}
                      onChange={(e) => setParts((rows) => rows.map((r) => (r._k === p._k ? { ...r, description: e.target.value } : r)))}
                      placeholder="Part description"
                      aria-label={`Part ${i + 1} description`}
                      className="min-w-40 flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
                    />
                    <input
                      type="number"
                      min={1}
                      value={p.quantity}
                      onChange={(e) => setParts((rows) => rows.map((r) => (r._k === p._k ? { ...r, quantity: Number(e.target.value) } : r)))}
                      onFocus={(e) => e.target.select()}
                      aria-label={`Part ${i + 1} quantity`}
                      className="w-16 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
                    />
                    <button type="button" onClick={() => setParts((rows) => rows.filter((r) => r._k !== p._k))} aria-label={`Remove part ${i + 1}`} className="rounded-ele border border-ele-danger px-2 py-1 text-xs text-ele-danger hover:bg-red-500/10">
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : sheet.partsUsed.length === 0 ? (
            <p className="text-sm text-ele-body opacity-70">None.</p>
          ) : (
            <>
              <ul className="space-y-1 text-sm text-ele-strong">
                {sheet.partsUsed.map((p, idx) => (
                  <li key={idx} className="flex justify-between gap-3">
                    <span>
                      {p.quantity} x {p.description || "Part"}
                      {p.partCode ? <span className="text-ele-body opacity-70"> ({p.partCode})</span> : null}
                    </span>
                    {p.unitCost != null && (
                      <span className="shrink-0 text-ele-body">{gbpExact(p.unitCost * p.quantity)}</span>
                    )}
                  </li>
                ))}
              </ul>
              {partsTotal(sheet.partsUsed) > 0 && (
                <p className="mt-1 text-sm text-ele-body">
                  Parts total: <span className="text-ele-strong">{gbpExact(partsTotal(sheet.partsUsed))}</span> (ex VAT)
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {!sheet.isCallout && (
            <label className="block text-sm">
              <span className="mb-1 block text-ele-body">Advisory notes</span>
              <textarea rows={2} value={advisoryNotes} onChange={(e) => setAdvisoryNotes(e.target.value)} className={editInputCls} />
            </label>
          )}
          <label className="block text-sm">
            <span className="mb-1 block text-ele-body">Engineer notes</span>
            <textarea rows={2} value={engineerNotes} onChange={(e) => setEngineerNotes(e.target.value)} className={editInputCls} />
          </label>
        </div>
      ) : (
        (sheet.advisoryNotes || sheet.engineerNotes) && (
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {sheet.advisoryNotes && (
              <div>
                <h3 className="mb-1 font-heading text-sm uppercase tracking-wide text-ele-blue">Advisory notes</h3>
                <p className="text-sm text-ele-body">{sheet.advisoryNotes}</p>
              </div>
            )}
            {sheet.engineerNotes && (
              <div>
                <h3 className="mb-1 font-heading text-sm uppercase tracking-wide text-ele-blue">Engineer notes</h3>
                <p className="text-sm text-ele-body">{sheet.engineerNotes}</p>
              </div>
            )}
          </div>
        )
      )}

      {/* Time on site for a service visit (Session ABU Part B); a call-out shows its own times in
          CalloutSummary above, so it is not duplicated here. While amending, the times and the
          visit date are editable (Session ADJ). */}
      {!sheet.isCallout &&
        (editing ? (
          <EditableVisitTimes
            visitDateYmd={visitDateYmd}
            onVisitDate={setVisitDateYmd}
            arrivalHHMM={arrivalHHMM}
            onArrival={setArrivalHHMM}
            departureHHMM={departureHHMM}
            onDeparture={setDepartureHHMM}
          />
        ) : (
          <TimeOnSiteSection sheet={sheet} />
        ))}

      <CoveredItemsSection sheet={sheet} />

      <RamsAcknowledgementSection sheet={sheet} />

      <RiskAssessmentSection sheet={sheet} onChanged={() => sheet.jobId && onAmended(sheet.jobId)} />

      <ProfitabilitySection p={sheet.profitability} />

      <SignatureSection sheet={sheet} />

      {/* The invoice back-reference for service/repair jobs (auto-billed via the job).
          A call-out's invoice is managed on the Call-outs page (its intake draft lives on
          the call-out, not the job), so it is not duplicated here. */}
      {sheet.jobId && !sheet.isCallout && <InvoiceBackrefSection jobId={sheet.jobId} initial={sheet.linkedInvoice} />}

      {/* Photos: an active record takes uploads (files save as soon as they are added, so they
          are independent of the amendment Save); an archived record is read-only. */}
      {sheet.jobId &&
        (archived ? (
          <JobPhotoGallery jobId={sheet.jobId} count={photoCount} emptyText="No photos taken." identify={{ source: "jobsheet", id: sheet.jobId }} />
        ) : (
          <div className="mt-5">
            <h3 className="mb-1 font-heading text-sm uppercase tracking-wide text-ele-blue">Photos</h3>
            <JobPhotoUploader
              key={sheet.jobId}
              jobId={sheet.jobId}
              initialCount={photoCount}
              label=""
              onCountChange={setPhotoCount}
              identify={{ source: "jobsheet", id: sheet.jobId }}
            />
          </div>
        ))}

      {editing && (
        <div className="mt-5 rounded-ele border border-ele-blue bg-ele-surface p-3">
          <label className="block text-sm">
            <span className="mb-1 block text-ele-body">Reason for amendment (optional)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. engineer confirmed by phone, or corrected a mis-recorded result" className={editInputCls} />
          </label>
          <p className="mt-2 text-xs text-ele-body opacity-80">
            Corrections are recorded in the sheet history. If this record has already been sent, you will be prompted to re-send the corrected version.
          </p>
          {editError && <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{editError}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              onClick={saveAmendment}
              disabled={saving || !dirty}
            >
              {saving ? "Saving..." : "Save amendment"}
            </Button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50"
            >
              Cancel
            </button>
            {!dirty && <span className="text-xs text-ele-body opacity-70">No changes yet.</span>}
          </div>
        </div>
      )}

      {invoiceReviewNote && (
        <p role="status" className="mt-3 rounded-ele border border-ele-blue bg-ele-surface p-3 text-sm text-ele-blue">
          This amendment changed the parts on a job that has a draft invoice. Review the draft in Xero and adjust it if needed. Nothing was changed on the invoice automatically.
        </p>
      )}

      {amendTimesheetNote && (
        <div role="alert" className="mt-3 rounded-ele border border-amber-500 bg-amber-500/10 p-3">
          <p className="text-sm text-amber-300">{amendTimesheetNote}</p>
        </div>
      )}

      {(sheet.amendments?.length ?? 0) > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Amendment history</h3>
          <ul className="space-y-1 text-sm text-ele-body">
            {(sheet.amendments ?? []).map((a, i) => (
              <li key={i} className="flex flex-wrap gap-x-2">
                <span className="text-ele-strong">{formatDate(a.at)}</span>
                {a.byName && <span className="opacity-70">by {a.byName}</span>}
                <span>{a.summary || "Amended"}</span>
                {a.afterSend && <span className="text-amber-400">(after sending)</span>}
                {/* Session ASM (the Director's D135 (b) ruling): the words of a fault note removed or replaced, not
                    only the count in the summary. */}
                {(a.defectsRemoved ?? []).map((text, j) => (
                  <span key={`r${j}`} className="w-full whitespace-pre-wrap pl-4 opacity-80">
                    Removed fault note: &ldquo;{text}&rdquo;
                  </span>
                ))}
                {(a.defectsAdded ?? []).map((text, j) => (
                  <span key={`a${j}`} className="w-full whitespace-pre-wrap pl-4 opacity-80">
                    Added fault note: &ldquo;{text}&rdquo;
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmDeleteModal
        open={showUndoBackdated}
        variant="destructive"
        title="Undo this backdated entry?"
        message="The visit goes back to Scheduled and the detail entered here is removed. Nothing has been sent to the customer yet. To enter it again, use Record as completed on the visit."
        confirmLabel="Undo entry"
        busy={undoingBackdated}
        error={undoBackdatedError}
        onConfirm={async () => {
          if (!sheet.jobId) return;
          setUndoingBackdated(true);
          setUndoBackdatedError(null);
          try {
            await api.jobsheets.revertBackdated(sheet.jobId);
            setShowUndoBackdated(false);
            onChanged();
          } catch (err) {
            setUndoBackdatedError(err instanceof ApiError && err.message ? err.message : "Could not undo the entry. Please try again.");
          } finally {
            setUndoingBackdated(false);
          }
        }}
        onCancel={() => { if (!undoingBackdated) { setShowUndoBackdated(false); setUndoBackdatedError(null); } }}
      />

      <ConfirmDeleteModal
        open={showDelete}
        variant={willArchive ? "archive" : "destructive"}
        title={willArchive ? "Archive this record?" : "Delete this record?"}
        message={
          willArchive
            ? "This record is completed (or has been invoiced or sent), so it will be archived and hidden from the active list, and can be reactivated."
            : "This record has not been completed, invoiced or sent, so it will be permanently deleted along with its photos. If it has been, it will be archived instead."
        }
        confirmLabel={willArchive ? "Archive record" : "Delete record"}
        busy={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) { setShowDelete(false); setDeleteError(null); } }}
      />
    </div>
  );
}

// ---- Self-loading wrapper (the lift detail page's inline expand) ----
// The lift page's service history only carries the job id, so this loads the full record via
// GET /api/jobsheets/:jobId and renders the same panel. A visit with no sheet on file (or a
// load failure) shows a plain message rather than jumping the office to another page.
export function ServiceRecordInline({
  jobId,
  onChanged,
  onDirtyChange,
}: {
  jobId: string;
  // Bubbled when the record changes in a way the parent page should reflect (archive/delete/
  // reactivate/return, approval, send). The panel itself reloads its own copy.
  onChanged?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [sheet, setSheet] = useState<JobSheetListItem | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "nosheet" | "error">("loading");

  function load(after?: () => void) {
    api.jobsheets
      .get(jobId)
      .then((s) => {
        setSheet(s);
        setState("ready");
        after?.();
      })
      .catch((err) => {
        setState(err instanceof ApiError && err.status === 404 ? "nosheet" : "error");
      });
  }

  useEffect(() => {
    setState("loading");
    setSheet(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the job changes
  }, [jobId]);

  if (state === "loading") return <Loader label="Loading the record..." shape="panel" className="p-3" />;
  if (state === "nosheet") return <p className="p-3 text-sm text-ele-body opacity-70">No service record sheet is on file for this visit.</p>;
  if (state === "error" || !sheet) {
    return (
      <p role="alert" className="p-3 text-sm text-ele-body">
        Could not load this record. Please try again in a moment.
      </p>
    );
  }
  return (
    <ServiceRecordPanel
      sheet={sheet}
      onSent={(_, sentAt, emailSentTo) => setSheet((s) => (s ? { ...s, sentToClientAt: sentAt, ...(emailSentTo !== undefined ? { emailSentTo } : {}) } : s))}
      onApproved={(_, res) => {
        setSheet((s) =>
          s ? { ...s, jobStatus: "complete", emailSentAt: new Date().toISOString(), emailSentTo: res.emailSentTo, driveFileUrl: res.driveFileUrl } : s,
        );
        onChanged?.();
      }}
      onChanged={() => load(onChanged)}
      onAmended={() => load(onChanged)}
      onDirtyChange={onDirtyChange}
    />
  );
}
